import * as React from 'react';
import { createPortal } from 'react-dom';
import { Brain } from 'lucide-react';
import apiService from '../../services/api';
import {
  CrossCheckClause,
  CrossCheckMatter,
  RawCrossCheckClause,
  CrossCheckDocumentType,
  mapRawClause,
  formatClauseNumberForDisplay,
} from '../../types/crossCheck';
import { CrossCheckClauseCard } from './CrossCheckClauseCard';
import { BrainShareReviewModal } from './BrainShareReviewModal';
import './BrainShareReviewModal.css';
import type { SubmitRevertContext } from '../../word/trackChanges/RedlineInserter';
import {
  isRicherSubmitRevertContext,
  submitRevertContextFromSubmissionStatus,
} from '../../utils/submitRevertContext';
import { AnalyzeAgainModal, AnalyzeAgainModalVariant } from './AnalyzeAgainModal';
import { BulkCloseConfirmModal } from './BulkCloseConfirmModal';
import { PastNegotiationSourcePdfPanel } from './PastNegotiationSourcePdfPanel';
import { PlaybookAgentBesidePanel } from './PlaybookAgentBesidePanel';
import { buildPlaybookContextFromRows } from '../../utils/playbookContext';
import {
  applyClauseEditorFilters,
  collectPastNegotiationAuthors,
  collectPastNegotiationYears,
  formatAuthorFilterLabel,
  formatSimilarityFilterLabel,
  formatTimeFilterLabel,
  isClauseClosable,
  type PastNegotiationTimeFilter,
  SIMILARITY_FILTER_MAX,
  SIMILARITY_FILTER_MIN,
  SIMILARITY_FILTER_PRESETS,
} from '../../utils/clauseEditorFilters';
import {
  acceptClausesSequentially,
  partitionClosableByTrackChanges,
} from '../../utils/clauseBulkCloseUtils';
import {
  buildClauseAskAgentPrefix,
  clauseHasAskAgentGrounding,
  clauseNeedsAskAgentContextSourceChoice,
  combineAskAgentContext,
  detectAskAgentLanguage,
  getAskAgentCopy,
  getAskAgentInitialQuestion,
  getAskAgentInitialQuestionForSource,
  getAskAgentNoMatchesNotice,
  getAskAgentNoMatchesNoPlaybookNotice,
  type AskAgentContextSource,
  type AskAgentDomain,
} from '../../utils/clauseAskAgentContext';
import type { SupportedLanguage } from '../../utils/languageDetection';
import { streamAiSuggestionFromChat } from '../../utils/crossCheckAiSuggestion';
import type { PlaybookAgentChatTurn } from './PlaybookAgentBesidePanel';
import type { PlaybookArchiveRow } from '../../utils/playbookRecordIndex';
import {
  buildMatchRankToArchiveRecordNum,
  buildPlaybookRecordNumberById,
} from '../../utils/playbookRecordIndex';
import { DocumentReader } from '../../word/api/DocumentReader';
import { DocumentStructureWatcher } from '../../word/api/DocumentStructureWatcher';
import { getClientRequestSearchText, buildParagraphSyncEntries, extractClauseFromRowContext, resolveTableRowClauseNumber } from '../../utils/crossCheckDocumentUtils';
import { crossCheckSolutionFromDomain, type CrossCheckSolution } from '../../utils/crossCheckSolution';
import { log } from '../../utils/logging';
import {
  isDebugLogPanelVisible,
  subscribeDebugLogPanelVisible,
} from '../../utils/addInPreferences';
import {
  clearDebugLog,
  copyDebugLogToClipboard,
  debugLog,
  debugWarn,
  getRemoteDebugBufferedCount,
  getRemoteDebugSessionId,
  subscribeDebugLog,
} from '../../utils/RemoteDebugLog';
import './CrossCheckResultsView.css';

const SA_BESIDE_SPLIT_PCT_KEY = 'courtyard-word_sa-source-beside-split-pct';
const DEFAULT_BESIDE_PDF_PCT = 47;
const MIN_BESIDE_PDF_PCT = 22;
const MAX_BESIDE_PDF_PCT = 78;

function readStoredBesidePdfPct(): number {
  try {
    const n = parseFloat(localStorage.getItem(SA_BESIDE_SPLIT_PCT_KEY) || '');
    if (Number.isFinite(n) && n >= MIN_BESIDE_PDF_PCT && n <= MAX_BESIDE_PDF_PCT) {
      return Math.round(n);
    }
  } catch {
    /* private mode */
  }
  return DEFAULT_BESIDE_PDF_PCT;
}

/** Collect unique playbook row IDs referenced by past_agreement options (Construction Loan). */
function collectAllPastAgreementPlaybookIds(clauses: CrossCheckClause[]): string[] {
  const set = new Set<string>();
  for (const c of clauses) {
    const pa = (c as any).past_agreement;
    if (!pa || !Array.isArray(pa.options)) continue;
    for (const opt of pa.options as { id?: string }[]) {
      const id = opt?.id;
      if (id && String(id).trim()) set.add(String(id).trim());
    }
  }
  return Array.from(set).sort();
}

// Tabs differ by domain:
//  - Master Cross Checks (and any non-CL flow): grouped by `documentType`.
//      'all' | 'sales_agreement' | 'appendix_a' | 'saved'
//  - Construction Loan: grouped by `matter` classification.
//      'all' | 'commercial' | 'legal' | 'other'
type DocTabId = 'all' | 'sales_agreement' | 'appendix_a' | 'saved';
type MatterTabId = 'all' | 'commercial' | 'legal' | 'other';
type TabId = DocTabId | MatterTabId;
type StepStatus = 'pending' | 'running' | 'complete' | 'error';

/**
 * Outcome of a single loadResults() call, used by the polling loop to decide
 * whether to keep polling, back off, or stop. `authError` means re-auth is
 * required and polling must stop (prevents the tokenless 401 storm).
 */
type LoadResult = { ok: boolean; authError: boolean };

export interface MccBesidePdfLayoutChangeDetail {
  /** True when the SA PDF viewer is open and portaled into TaskPane’s dock column. */
  docked: boolean;
  /** PDF column width % (for TaskPane grid); same as persisted beside split. */
  pdfWidthPct: number;
}

interface CrossCheckResultsViewProps {
  subProjectId: string;
  projectId?: string;
  /** Passed from TaskPane so the view can clear a stale pollingError and restart
   *  polling when the user logs back in after a forced/expired-token logout. */
  isAuthenticated?: boolean;
  /**
   * When set (MCC / Construction Loan in TaskPane), the SA PDF chrome is portaled
   * here so it spans the full clauses-view height beside Project Settings.
   */
  pdfBesideDockRef?: React.RefObject<HTMLDivElement | null>;
  /** Sibling column for the vertical resize handle (TaskPane). */
  besideHandleSlotRef?: React.RefObject<HTMLDivElement | null>;
  /** Width of this container for split drag math (usually `clauses-view`). */
  besideResizeOuterRef?: React.RefObject<HTMLElement | null>;
  /** Notifies TaskPane to toggle grid layout + CSS variable for PDF column width. */
  onMccBesidePdfLayoutChange?: (detail: MccBesidePdfLayoutChangeDetail) => void;
  /** TaskPane sets true after dock + handle slots mount (enables createPortal targets). */
  besidePdfChromeInDock?: boolean;
  /** Incremented when entering MCC/CL from the solutions list — scroll workflow steps into view. */
  workflowScrollRequestToken?: number;
  /** TaskPane MCC beside: scroll root for clause list when Ask Agent / SA PDF is docked. */
  besideRightScrollRef?: React.RefObject<HTMLElement | null>;
}

/** Nearest scrollable ancestor (overflow auto/scroll with overflow content). */
function findScrollableAncestor(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement;
  while (parent) {
    const { overflowY } = getComputedStyle(parent);
    if (
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

export const CrossCheckResultsView: React.FC<CrossCheckResultsViewProps> = ({
  subProjectId,
  projectId: projectIdProp,
  pdfBesideDockRef,
  besideHandleSlotRef,
  besideResizeOuterRef,
  onMccBesidePdfLayoutChange,
  besidePdfChromeInDock,
  workflowScrollRequestToken = 0,
  besideRightScrollRef,
  isAuthenticated = true,
}) => {
  const [projectId, setProjectId] = React.useState<string | undefined>(projectIdProp);
  const [parentProjectName, setParentProjectName] = React.useState<string | null>(null);
  const [detectedDomain, setDetectedDomain] = React.useState<'construction-loan' | 'real-estate' | null>(null);
  const crossCheckSolution = crossCheckSolutionFromDomain(detectedDomain);
  // Ref mirror so loadResults / sync can read the current domain without dep churn.
  const detectedDomainRef = React.useRef(detectedDomain);
  const crossCheckSolutionRef = React.useRef<CrossCheckSolution>(crossCheckSolution);
  React.useEffect(() => {
    detectedDomainRef.current = detectedDomain;
    crossCheckSolutionRef.current = crossCheckSolution;
  }, [detectedDomain, crossCheckSolution]);
  const [clauses, setClauses] = React.useState<CrossCheckClause[]>([]);
  /** Start true so we never flash the default "step 1 running" strip before the first fetch. */
  const [loading, setLoading] = React.useState(() => Boolean(subProjectId));
  const [error, setError] = React.useState<string | null>(null);
  /** Set during a silent poll failure so we can show a subtle indicator without wiping the existing results. */
  const [pollingError, setPollingError] = React.useState(false);
  /** True when polling has stopped after too many consecutive errors — shows the "retry" banner. */
  const [pollingStopped, setPollingStopped] = React.useState(false);
  /** Incrementing this restarts the poll effect (used by the "retry" button). */
  const [pollRetryKey, setPollRetryKey] = React.useState(0);
  const [syncingIndices, setSyncingIndices] = React.useState(false);
  const [showDebugLogPanel, setShowDebugLogPanel] = React.useState(() => isDebugLogPanelVisible());
  const [remoteDebugEntryCount, setRemoteDebugEntryCount] = React.useState(() => getRemoteDebugBufferedCount());
  const [remoteDebugCopyStatus, setRemoteDebugCopyStatus] = React.useState<string | null>(null);
  const [remoteDebugCopying, setRemoteDebugCopying] = React.useState(false);

  // When the user logs back in after a forced/expired-token logout the poll loop
  // has already exited (it stopped on the auth-failure path) and `pollingError`
  // is still true, showing a permanent "Reconnecting…" until the add-in is
  // refreshed.  Watching the isAuthenticated prop for a false→true edge lets us
  // clear the stale banner and restart polling without any user intervention.
  const prevIsAuthenticatedRef = React.useRef(isAuthenticated);
  React.useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current;
    prevIsAuthenticatedRef.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      // Re-authenticated: clear stale reconnecting state and restart the poll.
      setPollingError(false);
      setPollingStopped(false);
      setPollRetryKey((k) => k + 1);
      debugLog('CrossCheckResultsView: re-authenticated — restarting poll');
    }
  }, [isAuthenticated]);

  React.useEffect(
    () =>
      subscribeDebugLogPanelVisible(() => {
        setShowDebugLogPanel(isDebugLogPanelVisible());
      }),
    [],
  );

  React.useEffect(
    () =>
      subscribeDebugLog(() => {
        setRemoteDebugEntryCount(getRemoteDebugBufferedCount());
      }),
    [],
  );

  React.useEffect(() => {
    if (!remoteDebugCopyStatus) return;
    const t = window.setTimeout(() => setRemoteDebugCopyStatus(null), 5000);
    return () => window.clearTimeout(t);
  }, [remoteDebugCopyStatus]);
  const [activeTab, setActiveTab] = React.useState<TabId>('all');
  const [filterFinalVersion, setFilterFinalVersion] = React.useState(false);
  const [filterMinSimilarity, setFilterMinSimilarity] = React.useState<number | null>(null);
  const [similarityPanelOpen, setSimilarityPanelOpen] = React.useState(false);
  const [similaritySliderValue, setSimilaritySliderValue] = React.useState(SIMILARITY_FILTER_MAX);
  const similarityFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [filterSelectedAuthors, setFilterSelectedAuthors] = React.useState<Set<string> | null>(null);
  const [authorsPanelOpen, setAuthorsPanelOpen] = React.useState(false);
  const authorsFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [filterSelectedYears, setFilterSelectedYears] = React.useState<PastNegotiationTimeFilter>(null);
  const [timePanelOpen, setTimePanelOpen] = React.useState(false);
  const timeFilterRef = React.useRef<HTMLDivElement | null>(null);
  const [bulkClosing, setBulkClosing] = React.useState(false);
  const [bulkOpening, setBulkOpening] = React.useState(false);
  const [bulkClosePrompt, setBulkClosePrompt] = React.useState<{
    toLock: CrossCheckClause[];
    withTrackChanges: CrossCheckClause[];
    withoutTrackChanges: CrossCheckClause[];
  } | null>(null);
  const [bulkCloseBusyMessage, setBulkCloseBusyMessage] = React.useState<string | null>(null);
  const [updatingLockId, setUpdatingLockId] = React.useState<string | null>(null);
  const [submitRevertContextByClauseId, setSubmitRevertContextByClauseId] = React.useState<
    Record<string, SubmitRevertContext>
  >({});

  const syncSubmitRevertContextFromClauses = React.useCallback((nextClauses: CrossCheckClause[]) => {
    setSubmitRevertContextByClauseId((prev) => {
      const next = { ...prev };
      const activeIds = new Set(nextClauses.map((c) => c.id));
      for (const clause of nextClauses) {
        const serverCtx = submitRevertContextFromSubmissionStatus(clause.submission_status);
        if (serverCtx && isRicherSubmitRevertContext(serverCtx, next[clause.id])) {
          next[clause.id] = serverCtx;
        } else if (!clause.submission_status?.response_submitted) {
          delete next[clause.id];
        }
      }
      for (const id of Object.keys(next)) {
        if (!activeIds.has(id)) delete next[id];
      }
      return next;
    });
  }, []);

  React.useEffect(() => {
    if (!clauses.length) return;
    syncSubmitRevertContextFromClauses(clauses);
  }, [clauses, syncSubmitRevertContextFromClauses]);
  const [mutatingClauseIds, setMutatingClauseIds] = React.useState<Record<string, boolean>>({});
  const [analyzeAgainClause, setAnalyzeAgainClause] = React.useState<CrossCheckClause | null>(null);
  const [analyzeAgainVariant, setAnalyzeAgainVariant] =
    React.useState<AnalyzeAgainModalVariant>('mcc');
  const [actionBanner, setActionBanner] = React.useState<{ type: 'success' | 'error' | 'loading'; message: string } | null>(null);
  const [brainSharePendingCount, setBrainSharePendingCount] = React.useState(0);
  const [brainShareTotalCount, setBrainShareTotalCount] = React.useState(0);
  const [showBrainShareReviewModal, setShowBrainShareReviewModal] = React.useState(false);
  const [workflowStep, setWorkflowStep] = React.useState<number | null>(null);
  const [workflowStatus, setWorkflowStatus] = React.useState<string | null>(null);
  /** Full `cross_check_progress` from API — `status`/`workflow_step` alone miss some terminal shapes. */
  const [workflowProgressBlob, setWorkflowProgressBlob] = React.useState<Record<string, unknown> | null>(null);
  const [timerNowMs, setTimerNowMs] = React.useState<number>(Date.now());
  const stickyHeaderRef = React.useRef<HTMLDivElement | null>(null);
  const resultsMainColumnRef = React.useRef<HTMLDivElement | null>(null);
  const clauseCardHeaderRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const setClauseCardHeaderRef = React.useCallback(
    (clauseId: string) => (el: HTMLDivElement | null) => {
      if (el) clauseCardHeaderRefs.current.set(clauseId, el);
      else clauseCardHeaderRefs.current.delete(clauseId);
    },
    [],
  );
  /** Scroll target for "Identify sides / …" when entering MCC/CL from the solutions list. */
  const workflowStepsScrollAnchorRef = React.useRef<HTMLDivElement | null>(null);
  const consumedWorkflowScrollTokenRef = React.useRef(0);
  /** Only scroll after a real tab change — Strict Mode runs mount effects twice; a one-shot "skip first" ref still fires on the second run. */
  const prevActiveTabForScrollRef = React.useRef<TabId | null>(null);
  const syncInFlightRef = React.useRef(false);
  const lastSyncedSignatureRef = React.useRef<string>('');
  const prevClauseIdsSignatureRef = React.useRef<string>('');
  const lastSyncAttemptMsRef = React.useRef<number>(0);
  // mountMs is used as the fallback start time for step1 when the component mounts
  // before the first poll arrives (prevents the timer showing 00:00 initially).
  const mountMsRef = React.useRef<number>(Date.now());
  const stepTimingRef = React.useRef<{
    step1StartMs: number | null;
    step1ElapsedSec: number;
    step2StartMs: number | null;
    step2ElapsedSec: number;
  }>({
    step1StartMs: null,
    step1ElapsedSec: 0,
    step2StartMs: null,
    step2ElapsedSec: 0,
  });

  /**
   * Re-sync paragraph indices and clause numbers from the live Word document
   * using the current clauses state. Called both from loadResults() and from
   * the DocumentStructureWatcher when a structural change (paragraph added /
   * deleted) is detected — without triggering a full server reload.
   */
  const runParagraphSync = React.useCallback((currentClauses: CrossCheckClause[]) => {
    if (syncInFlightRef.current) return;

    const entries = buildParagraphSyncEntries(currentClauses, {
      solution: crossCheckSolutionRef.current,
    });

    if (entries.length === 0) return;

    syncInFlightRef.current = true;
    lastSyncAttemptMsRef.current = Date.now();
    DocumentReader.invalidateOoxmlParagraphTextsCache();
    // Invalidate the signature so the next loadResults() also re-runs sync.
    lastSyncedSignatureRef.current = '';
    setSyncingIndices(true);

    debugLog('CrossCheckResultsView: paragraph sync (structure change)', {
      entryCount: entries.length,
    });

    DocumentReader.syncParagraphIndicesFromDocument(entries)
      .then(({ indices, clauseNumbers }) => {
        debugLog('CrossCheckResultsView: paragraph sync (structure change)', {
          matched: Object.keys(indices).length,
          entries: entries.length,
        });

        const entryIds = new Set(entries.map((e) => e.id));
        setClauses((prev) =>
          prev.map((c) => {
            const nextIndex = indices[c.id];
            const nextClauseNumber = clauseNumbers[c.id];
            const wasSyncAttempted = entryIds.has(c.id);
            const shouldClearStaleIndex = wasSyncAttempted && nextIndex === undefined;
            const changed =
              nextIndex !== undefined || nextClauseNumber !== undefined || shouldClearStaleIndex;

            if (!changed) return c;

            return {
              ...c,
              ...(shouldClearStaleIndex && { paragraph_index: undefined }),
              ...(nextIndex !== undefined && { paragraph_index: nextIndex }),
              ...(nextClauseNumber !== undefined && { clause_number: nextClauseNumber }),
            };
          })
        );
      })
      .catch((err) => {
        console.warn('[CrossCheckResultsView] Paragraph sync (structure change) failed:', err);
      })
      .finally(() => {
        syncInFlightRef.current = false;
        setSyncingIndices(false);
      });
  }, []);

  // Re-sync paragraph indices at the natural moments the user looks at the
  // panel: when the add-in window regains focus or becomes visible. No
  // background polling — zero overhead while the user is typing in Word.
  const clausesRef = React.useRef<CrossCheckClause[]>(clauses);
  React.useEffect(() => {
    clausesRef.current = clauses;
  }, [clauses]);

  React.useEffect(() => {
    const watcher = DocumentStructureWatcher.getInstance();
    const unsubscribe = watcher.subscribe(() => {
      debugLog('CrossCheckResultsView: panel attention — re-syncing paragraph indices');
      runParagraphSync(clausesRef.current);
    });
    return unsubscribe;
  }, [runParagraphSync]);

  const toRawClauseFromIdentifiedRequest = React.useCallback((req: any, idx: number): RawCrossCheckClause => {
    const tableRowSupport = crossCheckSolutionRef.current === 'mcc';
    const paragraphIndex =
      req?.paragraph_index ??
      req?.track_changes?.paragraph_index ??
      undefined;
    const paragraphXml =
      req?.track_changes?.paragraph_xml ||
      req?.change_history?.paragraph_xml ||
      '';
    const editedText = req?.edited_text || req?.text || '';
    const originalText = req?.original_text || '';
    const isTableRow = tableRowSupport && req?.track_changes?.is_table_row === true;
    // Each table-row card is one TC paragraph — use its paragraph_xml like body Module B.
    const clientRequestValue =
      typeof paragraphXml === 'string' && paragraphXml.trim().length > 0
        ? paragraphXml
        : editedText || originalText || '';

    let clauseNumber = req?.clause_number;
    const rowContextText = req?.track_changes?.row_context_text;
    if (tableRowSupport) {
      clauseNumber = resolveTableRowClauseNumber(clauseNumber, req?.track_changes, {
        solution: crossCheckSolutionRef.current,
      });
    }
    if (
      tableRowSupport &&
      isTableRow &&
      (!String(clauseNumber ?? '').trim() || String(clauseNumber).trim() === 'לא נמצא')
    ) {
      const fromRowContext = extractClauseFromRowContext(
        typeof rowContextText === 'string' ? rowContextText : '',
      );
      if (fromRowContext) {
        clauseNumber = fromRowContext;
      }
    }

    const trackChangesParts =
      tableRowSupport ? req?.track_changes?.track_changes_parts : undefined;
    const mergedTrackChanges =
      req?.track_changes && typeof req.track_changes === 'object'
        ? {
            ...req.track_changes,
            ...(Array.isArray(trackChangesParts) && trackChangesParts.length > 0
              ? { track_changes_parts: trackChangesParts }
              : {}),
          }
        : req?.track_changes;

    return {
      id: String(req?.id ?? `identified_${idx + 1}`),
      clause_number: clauseNumber,
      documentType: req?.documentType || 'sales_agreement',
      // Match final cross-check behavior: prefer rendering XML for Client Request.
      client_request: clientRequestValue,
      original_text: originalText,
      edited_text: editedText,
      track_changes: mergedTrackChanges,
      participants: req?.participants,
      module_type: req?.module_type || 'module_b',
      paragraph_index: paragraphIndex != null ? Number(paragraphIndex) : undefined,
      track_changes_interpretation: req?.track_changes_interpretation,
      refered_file: req?.refered_file,
      text: req?.text,
      side: req?.side,
      // Construction Loan groups requests by `matter` (commercial / legal /
      // other) instead of `documentType`, so it must be carried through here.
      matter: req?.matter,
    };
  }, []);

  const loadResults = React.useCallback(async (silent = false): Promise<LoadResult> => {
    if (!subProjectId) {
      if (!silent) setLoading(false);
      return { ok: false, authError: false };
    }
    if (!silent) {
      setLoading(true);
      setWorkflowStep(null);
      setWorkflowStatus(null);
      setWorkflowProgressBlob(null);
    }
    setError(null);

    let result: LoadResult = { ok: false, authError: false };

    if (!silent) {
      debugLog('CrossCheckResultsView: loading results', { subProjectId });
    }

    try {
      const response = await apiService.getSubProjectDetails(subProjectId, detectedDomainRef.current ?? undefined);

      if (response.success && response.data) {
        if (response.data.parent_project_id && !projectIdProp) {
          setProjectId(response.data.parent_project_id);
        }
        const pname =
          response.data.parent_project_name ||
          response.data.project_name ||
          response.data.name;
        if (typeof pname === 'string' && pname.trim()) {
          setParentProjectName(pname.trim());
        }
        // Capture the domain detected by getSubProjectDetails so that playbook
        // resolution calls are routed to the correct backend prefix.
        const responseDomain = (response as any).detected_domain;
        if (responseDomain === 'construction-loan' || responseDomain === 'real-estate') {
          setDetectedDomain(responseDomain);
        }
        const progress = response.data.cross_check_progress || null;
        const progressObj =
          progress && typeof progress === 'object' && !Array.isArray(progress)
            ? (progress as Record<string, unknown>)
            : null;
        setWorkflowProgressBlob(progressObj);
        setWorkflowStep(
          typeof progressObj?.workflow_step === 'number'
            ? progressObj.workflow_step
            : progress?.workflow_step ?? null
        );
        setWorkflowStatus(
          typeof progressObj?.status === 'string' ? progressObj.status : progress?.status ?? null
        );

        const rawCrossCheckResults: RawCrossCheckClause[] = Array.isArray(response.data.cross_checks_results)
          ? response.data.cross_checks_results
          : [];
        // Moves archive the prior card as `stale: true` snapshots in cross_checks_results.
        // Hide those rows in the UI (dashboard does the same) so reload does not show a
        // ghost copy of the clause in its original tab.
        const activeCrossCheckResults = rawCrossCheckResults.filter((c) => !c.stale);
        const rawIdentifiedRequests = Array.isArray(response.data?.client_requests_assignments_results?.requests)
          ? response.data.client_requests_assignments_results.requests
          : [];

        // Show identified requests immediately (Step 1/2) when full cross-check
        // results are not ready yet; once cross_checks_results exists, prefer it.
        const raw: RawCrossCheckClause[] = activeCrossCheckResults.length > 0
          ? activeCrossCheckResults
          : rawIdentifiedRequests.map(toRawClauseFromIdentifiedRequest);

        // Build a set of "our_side" author names from sub-project metadata.
        // Clause cards whose every participant is our_side are hidden – they
        // represent our own redlines and should never appear in the results.
        // API returns sub_project_metadata (not metadata); without this, sideAssignments was always {}.
        const meta = response.data?.sub_project_metadata ?? response.data?.metadata ?? {};
        const sideAssignments: Record<string, string> =
          meta?.side_assignments_data?.side_assignments ?? {};
        const isOurSideOnly = (clause: RawCrossCheckClause): boolean => {
          const participants: string[] = Array.isArray((clause as any).participants)
            ? (clause as any).participants
            : [];
          if (!participants.length) return false;
          return participants.every((p) => sideAssignments[p] === 'our_side');
        };

        const filtered = Object.keys(sideAssignments).length > 0
          ? raw.filter((c) => !isOurSideOnly(c))
          : raw;
        const enableTableRowSupport = crossCheckSolutionRef.current === 'mcc';
        const mapOptions = { enableTableRowSupport };
        const syncCtx = { solution: crossCheckSolutionRef.current };
        const mapped = filtered.map((rawClause) => mapRawClause(rawClause, mapOptions));

        // Merge with previous clauses so later polling refreshes do not downgrade
        // already-synced client-side clause numbers (e.g. "8.7.1") back to
        // backend placeholders such as "לא נמצא".
        const isUnknownClauseNumber = (value: unknown): boolean => {
          const v = String(value ?? '').trim();
          return (
            !v ||
            v === 'לא נמצא' ||
            v.toLowerCase() === 'not found' ||
            v.toLowerCase() === 'n/a' ||
            v === '-'
          );
        };

        const clauseIdsSignature = mapped
          .map((c) => c.id)
          .sort()
          .join('|');
        if (clauseIdsSignature !== prevClauseIdsSignatureRef.current) {
          prevClauseIdsSignatureRef.current = clauseIdsSignature;
          lastSyncedSignatureRef.current = '';
        }

        setClauses((prev) => {
          if (!prev.length || !silent) return mapped;
          const prevById = new Map(prev.map((c) => [c.id, c]));
          const merged = mapped.map((nextClause) => {
            const prevClause = prevById.get(nextClause.id);
            if (!prevClause) return nextClause;

            const keepPrevClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              isUnknownClauseNumber(nextClause.clause_number);
            // If we already synced a concrete clause number for the same paragraph,
            // keep it authoritative over backend placeholder numbering (e.g. 4.7/6.4).
            const keepPrevSyncedClauseNumber =
              !isUnknownClauseNumber(prevClause.clause_number) &&
              !isUnknownClauseNumber(nextClause.clause_number) &&
              String(prevClause.clause_number) !== String(nextClause.clause_number) &&
              prevClause.paragraph_index != null &&
              nextClause.paragraph_index != null &&
              Number(prevClause.paragraph_index) === Number(nextClause.paragraph_index);
            const keepPrevParagraphIndex =
              prevClause.paragraph_index != null && nextClause.paragraph_index == null;

            if (!keepPrevClauseNumber && !keepPrevSyncedClauseNumber && !keepPrevParagraphIndex) {
              return nextClause;
            }

            return {
              ...nextClause,
              ...((keepPrevClauseNumber || keepPrevSyncedClauseNumber) && {
                clause_number: prevClause.clause_number,
              }),
              ...(keepPrevParagraphIndex && { paragraph_index: prevClause.paragraph_index }),
            };
          });

          return merged;
        });

        // Sync paragraph indices in the document (non-blocking): find correct indices by content.
        // Guard against repeated polling-driven sync loops: only sync when source clauses changed.
        const entries = buildParagraphSyncEntries(mapped, syncCtx);
        const syncSignature = JSON.stringify(
          entries.map((e) => [e.id, e.searchText.slice(0, 200)])
        );
        const hasUnresolvedParagraphIndex = mapped.some(
          (c) =>
            getClientRequestSearchText(c, syncCtx).trim().length > 0 &&
            (c as any).paragraph_index == null
        );
        const canRetryUnresolvedSync =
          hasUnresolvedParagraphIndex &&
          Date.now() - lastSyncAttemptMsRef.current > 10000;
        const forceSyncForExplicitLoad = !silent;
        const shouldRunSync =
          entries.length > 0 &&
          !syncInFlightRef.current &&
          (forceSyncForExplicitLoad || syncSignature !== lastSyncedSignatureRef.current || canRetryUnresolvedSync);

        if (shouldRunSync) {
          syncInFlightRef.current = true;
          lastSyncAttemptMsRef.current = Date.now();
          lastSyncedSignatureRef.current = syncSignature;
          setSyncingIndices(true);

          debugLog('CrossCheckResultsView: paragraph sync starting', {
            subProjectId,
            entryCount: entries.length,
            forceSyncForExplicitLoad,
            hasUnresolvedParagraphIndex,
            canRetryUnresolvedSync,
            syncSignatureLen: syncSignature.length,
            clausesBeforeSync: mapped.map((c) => ({
              id: c.id,
              clauseNumber: c.clause_number,
              paragraphIndex: c.paragraph_index ?? null,
              searchTextLen: getClientRequestSearchText(c, syncCtx).trim().length,
            })),
          });

          DocumentReader.syncParagraphIndicesFromDocument(entries)
            .then(({ indices, clauseNumbers }) => {
              debugLog('CrossCheckResultsView: paragraph sync', {
                matched: Object.keys(indices).length,
                entries: entries.length,
              });

              const entryIds = new Set(entries.map((e) => e.id));
              const syncChanges: Array<Record<string, unknown>> = [];
              setClauses((prev) =>
                prev.map((c) => {
                  const nextIndex = indices[c.id];
                  const nextClauseNumber = clauseNumbers[c.id];
                  const wasSyncAttempted = entryIds.has(c.id);
                  const shouldClearStaleIndex = wasSyncAttempted && nextIndex === undefined;
                  const changed = nextIndex !== undefined || nextClauseNumber !== undefined || shouldClearStaleIndex;

                  if (!changed) return c;

                  if (wasSyncAttempted) {
                    syncChanges.push({
                      clauseId: c.id,
                      clauseNumber: c.clause_number,
                      prevParagraphIndex: c.paragraph_index ?? null,
                      nextParagraphIndex: shouldClearStaleIndex ? null : (nextIndex ?? c.paragraph_index ?? null),
                      nextClauseNumber: nextClauseNumber ?? null,
                      clearedStaleIndex: shouldClearStaleIndex,
                    });
                  }

                  return {
                    ...c,
                    ...(shouldClearStaleIndex && { paragraph_index: undefined }),
                    ...(nextIndex !== undefined && { paragraph_index: nextIndex }),
                    ...(nextClauseNumber !== undefined && { clause_number: nextClauseNumber }),
                  };
                })
              );

              const unmatched = entries
                .filter((e) => indices[e.id] === undefined)
                .map((e) => ({
                  clauseId: e.id,
                  searchSnippet: e.searchText.replace(/\s+/g, ' ').trim().slice(0, 120),
                }));

              debugLog('CrossCheckResultsView: paragraph sync completed', {
                matched: Object.keys(indices).length,
                totalEntries: entries.length,
                unmatchedCount: unmatched.length,
                unmatched,
                syncChanges,
                resolvedClauseNumbers: clauseNumbers,
              });
            })
            .catch((err) => {
              debugWarn('CrossCheckResultsView: paragraph sync failed', {
                message: (err as Error)?.message ?? String(err),
              });
              console.warn('[CrossCheckResultsView] Paragraph index sync failed:', err);
            })
            .finally(() => {
              syncInFlightRef.current = false;
              setSyncingIndices(false);
            });
        } else if (!syncInFlightRef.current) {
          setSyncingIndices(false);
          if (entries.length > 0) {
            debugLog('CrossCheckResultsView: paragraph sync skipped', {
              entryCount: entries.length,
              syncInFlight: syncInFlightRef.current,
              signatureUnchanged: syncSignature === lastSyncedSignatureRef.current,
              hasUnresolvedParagraphIndex,
            });
          }
        }
      } else {
        debugLog('CrossCheckResultsView: no data from server');
        setClauses([]);
      }
      result = { ok: true, authError: false };
    } catch (err: any) {
      log('CrossCheckResultsView: load failed', { message: err?.message ?? String(err) });
      console.error('[CrossCheckResultsView] Error loading results:', err);
      const isAuthError = err?.isAuthError === true || err?.response?.status === 401;
      if (silent) {
        // Keep whatever is already on screen — wipe it only on a user-initiated load.
        // Show a subtle reconnecting indicator instead of an error banner.
        setPollingError(true);
      } else {
        setError(err.message || 'Failed to load cross-check results');
        setClauses([]);
      }
      result = { ok: false, authError: isAuthError };
    } finally {
      if (!silent) setLoading(false);
    }
    return result;
  }, [subProjectId, toRawClauseFromIdentifiedRequest, projectIdProp]);

  const handleToggleLock = React.useCallback(async (clause: CrossCheckClause) => {
    if (!subProjectId || !clause.id) return;
    setUpdatingLockId(clause.id);
    try {
      const response = await apiService.toggleClauseLock(subProjectId, clause.id);
      if (response?.success) {
        const newLocked = !clause.lock_status?.locked;
        setClauses((prev) =>
          prev.map((c) =>
            c.id === clause.id
              ? { ...c, lock_status: { ...(c.lock_status || {}), locked: newLocked } }
              : c
          )
        );
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Toggle lock failed:', err);
    } finally {
      setUpdatingLockId(null);
    }
  }, [subProjectId]);

  const handleClauseEdited = React.useCallback((updated: CrossCheckClause) => {
    setClauses((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, []);

  const handleActionBanner = React.useCallback(
    (banner: { type: 'success' | 'error'; message: string }) => {
      setActionBanner(banner);
    },
    [],
  );

  const fetchBrainShareCounts = React.useCallback(async () => {
    if (!subProjectId) return;
    try {
      const response = await apiService.getUserBrainShares(subProjectId);
      if (response.success) {
        const entries = (response.data as Array<{ status?: string }>) || [];
        setBrainSharePendingCount(entries.filter((entry) => entry.status === 'in_process').length);
        setBrainShareTotalCount(entries.length);
      }
    } catch (err) {
      console.error('Error fetching BrainShare counts:', err);
    }
  }, [subProjectId]);

  React.useEffect(() => {
    void fetchBrainShareCounts();
  }, [fetchBrainShareCounts]);

  const handleBrainShareUpdated = React.useCallback(() => {
    setBrainShareTotalCount((prev) => prev + 1);
    setBrainSharePendingCount((prev) => prev + 1);
    void fetchBrainShareCounts();
  }, [fetchBrainShareCounts]);

  const handleResponseSubmitted = React.useCallback(
    (clauseId: string, revertContext?: SubmitRevertContext) => {
      setClauses((prev) =>
        prev.map((c) =>
          c.id === clauseId
            ? {
                ...c,
                submission_status: {
                  ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                  response_submitted: true,
                  response_submitted_at: new Date().toISOString(),
                  response_revert_context: revertContext ?? null,
                },
              }
            : c
        )
      );
      if (revertContext) {
        setSubmitRevertContextByClauseId((prev) => ({ ...prev, [clauseId]: revertContext }));
      }
    },
    [],
  );

  const applyServerCrossChecksUpdate = React.useCallback((nextCrossChecks: RawCrossCheckClause[] | undefined | null) => {
    if (!Array.isArray(nextCrossChecks)) return;

    const activeRaw = nextCrossChecks.filter((c) => !c.stale);
    const activeIds = new Set(activeRaw.map((r) => r.id));
    const enableTableRowSupport = crossCheckSolutionRef.current === 'mcc';
    const mapped = activeRaw.map((rawClause) =>
      mapRawClause(rawClause, { enableTableRowSupport }),
    );

    setClauses((prev) => {
      const byId = new Map(prev.map((c) => [c.id, c]));
      mapped.forEach((updated) => {
        byId.set(updated.id, updated);
      });
      return Array.from(byId.values()).filter((c) => activeIds.has(c.id));
    });
  }, []);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    const anyErr = err as {
      response?: { data?: { message?: string; detail?: string | Array<{ msg?: string } | string> } };
      message?: string;
    };
    const detail = anyErr?.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail
        .map((d) => (typeof d === 'object' && d && 'msg' in d ? String(d.msg) : String(d)))
        .join('; ');
    }
    return anyErr?.response?.data?.message || anyErr?.message || fallback;
  };

  const handleMoveClause = React.useCallback(async (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => {
    if (!subProjectId) return;
    if (clause.documentType === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const isRevertToOrigin =
      !!clause.origin_document_type && target === clause.origin_document_type;

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({
      type: 'loading',
      message: isRevertToOrigin
        ? 'Restoring original category...'
        : target === 'saved_for_later'
          ? 'Moving to Saved...'
          : 'Moving and re-analyzing clause...',
    });

    try {
      const response = await apiService.moveClause(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);

      const targetLabel = target === 'sales_agreement'
        ? 'Sales Agreement'
        : target === 'appendix_a'
          ? 'Appendix A'
          : 'Saved';
      // MCC tabs are by documentType; Construction Loan tabs are by matter — skip tab jump on CL.
      if (detectedDomain !== 'construction-loan') {
        const tabKey: TabId = target === 'saved_for_later' ? 'saved' : target;
        setActiveTab(tabKey);
      }

      const reverted = (response.data as { reverted?: boolean } | undefined)?.reverted === true;
      setActionBanner({
        type: 'success',
        message: reverted
          ? `Clause restored to ${targetLabel}`
          : `Clause moved to ${targetLabel}`,
      });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate, detectedDomain]);

  const handleMoveClauseMatter = React.useCallback(async (
    clause: CrossCheckClause,
    target: CrossCheckMatter,
  ) => {
    if (!subProjectId) return;
    const currentMatter = clause.matter || 'other';
    if (currentMatter === target) {
      setActionBanner({ type: 'success', message: 'Clause is already in that category.' });
      return;
    }

    const submissionStatus = clause.submission_status;
    if (submissionStatus?.schedule_submitted || submissionStatus?.response_submitted) {
      setActionBanner({
        type: 'error',
        message: 'This clause has already been submitted. Undo the submission before moving it.',
      });
      return;
    }

    const targetLabel =
      target === 'commercial' ? 'Commercial' : target === 'legal' ? 'Legal' : 'Other';

    setMutatingClauseIds((prev) => ({ ...prev, [clause.id]: true }));
    setActionBanner({ type: 'loading', message: `Moving to ${targetLabel}...` });

    try {
      const response = await apiService.moveClauseMatter(subProjectId, clause.id, target);
      if (!response?.success) {
        throw new Error(response?.message || 'Failed to move clause');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActiveTab(target);
      setActionBanner({ type: 'success', message: `Clause moved to ${targetLabel}` });
    } catch (err) {
      console.error('[CrossCheckResultsView] Move clause matter failed:', err);
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Failed to move clause'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clause.id];
        return next;
      });
    }
  }, [subProjectId, applyServerCrossChecksUpdate]);

  const handleAnalyzeAgainOpen = React.useCallback((clause: CrossCheckClause) => {
    const isCl =
      detectedDomain === 'construction-loan' ||
      clause.matter === 'commercial' ||
      clause.matter === 'legal' ||
      clause.matter === 'other';
    setAnalyzeAgainVariant(isCl ? 'cl-past-negotiations' : 'mcc');
    setAnalyzeAgainClause(clause);
  }, [detectedDomain]);

  const handleAnalyzeAgainSubmit = React.useCallback(async (payload: {
    salesAgreementFeedback?: string;
    bankOfChangesFeedback?: string;
    pastNegotiationsFeedback?: string;
  }) => {
    if (!analyzeAgainClause || !subProjectId) {
      setActionBanner({ type: 'error', message: 'Missing context for re-analysis' });
      return;
    }

    const clause = analyzeAgainClause;
    const clauseId = clause.id;
    const clauseLabel = clause.clause_number ? `Clause ${formatClauseNumberForDisplay(clause.clause_number)}` : 'clause';

    // Close modal immediately so the user can keep working while re-analysis runs.
    setAnalyzeAgainClause(null);

    debugLog('CrossCheckResultsView: analyze again submit', {
      clauseId,
      variant: analyzeAgainVariant,
      subProjectId,
    });

    setMutatingClauseIds((prev) => ({ ...prev, [clauseId]: true }));
    const isClReanalyze = analyzeAgainVariant === 'cl-past-negotiations';
    setActionBanner({
      type: 'loading',
      message: isClReanalyze
        ? `Re-analyzing past negotiations for ${clauseLabel}...`
        : `Re-analyzing ${clauseLabel}...`,
    });

    try {
      const response = isClReanalyze
        ? await apiService.reanalyzePastNegotiations(
            subProjectId,
            clauseId,
            payload.pastNegotiationsFeedback || '',
          )
        : await apiService.reanalyzeClause(subProjectId, clauseId, {
            sales_agreement_feedback: payload.salesAgreementFeedback,
            bank_of_changes_feedback: payload.bankOfChangesFeedback,
          });
      if (!response?.success) {
        throw new Error(response?.message || 'Re-analysis failed');
      }

      const updated = (response.data as { updated_cross_checks_results?: RawCrossCheckClause[] } | undefined)
        ?.updated_cross_checks_results;
      applyServerCrossChecksUpdate(updated);
      setActionBanner({
        type: 'success',
        message: isClReanalyze
          ? `${clauseLabel} past negotiations re-analyzed`
          : `${clauseLabel} re-analyzed`,
      });
      debugLog('CrossCheckResultsView: analyze again success', { clauseId });
    } catch (err) {
      console.error('[CrossCheckResultsView] Reanalyze clause failed:', err);
      debugWarn('CrossCheckResultsView: analyze again failed', {
        clauseId,
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
      setActionBanner({
        type: 'error',
        message: extractApiErrorMessage(err, 'Re-analysis failed'),
      });
    } finally {
      setMutatingClauseIds((prev) => {
        const next = { ...prev };
        delete next[clauseId];
        return next;
      });
    }
  }, [analyzeAgainClause, analyzeAgainVariant, subProjectId, applyServerCrossChecksUpdate]);

  React.useEffect(() => {
    if (!actionBanner || actionBanner.type === 'loading') return;
    const timer = window.setTimeout(() => setActionBanner(null), 5000);
    return () => window.clearTimeout(timer);
  }, [actionBanner]);

  const handleResponseUndone = React.useCallback((clauseId: string) => {
    setClauses((prev) =>
      prev.map((c) =>
        c.id === clauseId
          ? {
              ...c,
              submission_status: {
                ...(c.submission_status || { schedule_submitted: false, response_submitted: false }),
                response_submitted: false,
                response_submitted_at: undefined,
                response_revert_context: undefined,
              },
            }
          : c
      )
    );
    setSubmitRevertContextByClauseId((prev) => {
      const next = { ...prev };
      delete next[clauseId];
      return next;
    });
  }, []);

  /** Only changes when past_agreement payloads change — not on paragraph_index / sync-only updates. */
  const pastAgreementDataFingerprint = React.useMemo(
    () => clauses.map((c) => JSON.stringify((c as any).past_agreement ?? null)).join('\n'),
    [clauses]
  );

  const [playbookXmlById, setPlaybookXmlById] = React.useState<Record<string, string>>({});
  const [playbookSideById, setPlaybookSideById] = React.useState<
    Record<string, 'our_side' | 'their_side'>
  >({});
  const [playbookFileNameById, setPlaybookFileNameById] = React.useState<Record<string, string>>({});
  const [playbookFileIdById, setPlaybookFileIdById] = React.useState<Record<string, string>>({});
  const [playbookFinalVersionById, setPlaybookFinalVersionById] = React.useState<Record<string, boolean>>({});
  const [playbookPlainTextById, setPlaybookPlainTextById] = React.useState<Record<string, string>>({});
  const [playbookAuthorById, setPlaybookAuthorById] = React.useState<Record<string, string>>({});
  const [playbookTimestampById, setPlaybookTimestampById] = React.useState<Record<string, string>>({});
  const [playbooksBatchLoading, setPlaybooksBatchLoading] = React.useState(false);

  const allPlaybookIdsForBatch = React.useMemo(
    () => collectAllPastAgreementPlaybookIds(clauses),
    [pastAgreementDataFingerprint]
  );

  React.useEffect(() => {
    const resolvedProjectId = projectId ?? projectIdProp;
    if (!resolvedProjectId) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookFinalVersionById({});
      setPlaybookPlainTextById({});
      setPlaybookAuthorById({});
      setPlaybookTimestampById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    const ids = allPlaybookIdsForBatch;
    if (ids.length === 0) {
      setPlaybookXmlById({});
      setPlaybookSideById({});
      setPlaybookFileNameById({});
      setPlaybookFileIdById({});
      setPlaybookFinalVersionById({});
      setPlaybookPlainTextById({});
      setPlaybookAuthorById({});
      setPlaybookTimestampById({});
      setPlaybooksBatchLoading(false);
      return;
    }
    if (detectedDomain === null) {
      setPlaybooksBatchLoading(true);
      return;
    }

    let cancelled = false;
    setPlaybooksBatchLoading(true);
    void (async () => {
      try {
        const response = await apiService.getPastContractsPlaybooksByIds(
          resolvedProjectId,
          ids,
          detectedDomain ?? 'construction-loan',
        );
        if (cancelled) return;
        if (!response.success || !Array.isArray(response.data)) {
          console.warn('CrossCheckResultsView: past agreement playbooks response invalid');
          if (!cancelled) {
            setPlaybookXmlById({});
            setPlaybookSideById({});
            setPlaybookFileNameById({});
            setPlaybookFileIdById({});
            setPlaybookFinalVersionById({});
            setPlaybookPlainTextById({});
            setPlaybookAuthorById({});
            setPlaybookTimestampById({});
          }
          return;
        }
        const rows = response.data as Array<{
          id?: string;
          paragraph_xml?: string;
          paragraph_text?: string;
          side?: string;
          source_filename?: string;
          source_doc_type?: string;
          file_id?: string;
          author?: string;
          timestamp?: string;
        }>;
        const byId: Record<string, string> = {};
        const sideById: Record<string, 'our_side' | 'their_side'> = {};
        const fileNameById: Record<string, string> = {};
        const fileIdById: Record<string, string> = {};
        const finalVersionById: Record<string, boolean> = {};
        const plainTextById: Record<string, string> = {};
        const authorById: Record<string, string> = {};
        const timestampById: Record<string, string> = {};
        for (const row of rows) {
          if (row?.id && row?.paragraph_xml) {
            byId[row.id] = row.paragraph_xml;
          }
          if (row?.id && (row?.side === 'our_side' || row?.side === 'their_side')) {
            sideById[row.id] = row.side;
          }
          if (row?.id) {
            if (row.source_filename?.trim()) {
              fileNameById[row.id] = row.source_filename.trim();
            }
            if (row.file_id?.trim()) {
              fileIdById[row.id] = row.file_id.trim();
            }
            if (row.source_doc_type === 'final_version') {
              finalVersionById[row.id] = true;
            }
            if (row.paragraph_text?.trim()) {
              plainTextById[row.id] = row.paragraph_text.trim();
            }
            if (row.author?.trim()) {
              authorById[row.id] = row.author.trim();
            }
            if (row.timestamp?.trim()) {
              timestampById[row.id] = row.timestamp.trim();
            }
          }
        }
        debugLog('CrossCheckResultsView: past agreement playbooks loaded', {
          requestedIds: ids.length,
          rows: rows.length,
        });
        setPlaybookXmlById(byId);
        setPlaybookSideById(sideById);
        setPlaybookFileNameById(fileNameById);
        setPlaybookFileIdById(fileIdById);
        setPlaybookFinalVersionById(finalVersionById);
        setPlaybookPlainTextById(plainTextById);
        setPlaybookAuthorById(authorById);
        setPlaybookTimestampById(timestampById);
      } catch (e) {
        debugWarn('CrossCheckResultsView: past agreement playbooks failed', {
          message: e instanceof Error ? e.message : String(e),
        });
        if (!cancelled) {
          setPlaybookXmlById({});
          setPlaybookSideById({});
          setPlaybookFileNameById({});
          setPlaybookFileIdById({});
          setPlaybookFinalVersionById({});
          setPlaybookPlainTextById({});
          setPlaybookAuthorById({});
          setPlaybookTimestampById({});
        }
      } finally {
        if (!cancelled) setPlaybooksBatchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, projectIdProp, allPlaybookIdsForBatch, detectedDomain]);

  // ─── Sales Agreement "Go to" split-view state ─────────────────────────────
  // When the user clicks the "Go to" badge on a clause's Sales Agreement section,
  // we fetch the SA source PDF (highlighted on the matched page) and show it in a
  // split view: PDF on the left, clause list on the right (TaskPane portaled dock).
  type SalesAgreementSourceView = {
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    buffer: ArrayBuffer | null;
    page: number;
    total: number;
    /** Original clause text — drives the panel's client-side fuzzy highlight + scroll. */
    searchText: string;
  };
  const [salesAgreementSourceView, setSalesAgreementSourceView] =
    React.useState<SalesAgreementSourceView | null>(null);

  type PlaybookAgentBesideView = {
    sessionKey: string;
    clauseId: string;
    clauseNumberDisplay: string | null;
    loading: boolean;
    error: string | null;
    contextBody: string;
    projectLabel: string;
    recordCount: number;
    playbookRows: PlaybookArchiveRow[];
    matchRankToArchiveRecordNum: Map<number, number>;
    playbookBodyCached: string;
    askDomain: AskAgentDomain;
    askLanguage: SupportedLanguage;
    initialQuestion: string | null;
    openingAssistantMessage: string | null;
    awaitingDeepResearchConsent: boolean;
    awaitingContextSourceChoice: boolean;
    contextSource: AskAgentContextSource | null;
  };
  const [playbookAgentBeside, setPlaybookAgentBeside] =
    React.useState<PlaybookAgentBesideView | null>(null);
  const playbookAgentLoadRef = React.useRef(0);
  const [agentAiStreamingClauseId, setAgentAiStreamingClauseId] = React.useState<string | null>(
    null,
  );
  const [agentSuggestedXmlByClauseId, setAgentSuggestedXmlByClauseId] = React.useState<
    Record<string, string>
  >({});

  /** Tracks the in-flight target clause id so a slow response from a previous clause
   *  doesn't overwrite the panel after the user clicked a different clause. */
  const salesAgreementSourceTargetRef = React.useRef<string | null>(null);

  /** PDF column width (%); persisted; adjusted via drag handle. */
  const [saBesidePdfWidthPct, setSaBesidePdfWidthPct] = React.useState(readStoredBesidePdfPct);
  const saBesidePdfWidthPctRef = React.useRef(saBesidePdfWidthPct);
  React.useEffect(() => {
    saBesidePdfWidthPctRef.current = saBesidePdfWidthPct;
  }, [saBesidePdfWidthPct]);

  const resultsViewRef = React.useRef<HTMLDivElement>(null);
  const besideResizeDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    startPct: number;
    containerWidth: number;
  } | null>(null);

  const persistBesidePdfPct = React.useCallback((pct: number) => {
    const clamped = Math.round(
      Math.min(MAX_BESIDE_PDF_PCT, Math.max(MIN_BESIDE_PDF_PCT, pct)),
    );
    try {
      localStorage.setItem(SA_BESIDE_SPLIT_PCT_KEY, String(clamped));
    } catch {
      /* ignore */
    }
    return clamped;
  }, []);

  const handleBesideResizePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const root = besideResizeOuterRef?.current ?? resultsViewRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      besideResizeDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startPct: saBesidePdfWidthPctRef.current,
        containerWidth: Math.max(1, rect.width),
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      e.preventDefault();
    },
    [besideResizeOuterRef],
  );

  const handleBesideResizePointerMove = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    const delta = e.clientX - d.startX;
    const deltaPct = (delta / d.containerWidth) * 100;
    const next = Math.min(
      MAX_BESIDE_PDF_PCT,
      Math.max(MIN_BESIDE_PDF_PCT, d.startPct + deltaPct),
    );
    setSaBesidePdfWidthPct(Math.round(next * 10) / 10);
  }, []);

  const scheduleScrollActiveBesideClauseRef = React.useRef<() => void>(() => {});
  const prevBesidePanelOpenRef = React.useRef(false);
  /** Clause to re-align under sticky tabs after the beside panel closes (X / toggle-off only). */
  const pendingCloseClauseScrollRef = React.useRef<string | null>(null);

  const endBesideResizeDrag = React.useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = besideResizeDragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    besideResizeDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* already released */
    }
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    let delta = 0;
    if (e.key === 'ArrowLeft') delta = -2;
    else if (e.key === 'ArrowRight') delta = 2;
    else if (e.key === 'Home') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MIN_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else if (e.key === 'End') {
      e.preventDefault();
      setSaBesidePdfWidthPct(persistBesidePdfPct(MAX_BESIDE_PDF_PCT));
      requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
      return;
    } else return;
    e.preventDefault();
    setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev + delta));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const handleBesideResizeDoubleClick = React.useCallback(() => {
    setSaBesidePdfWidthPct(persistBesidePdfPct(DEFAULT_BESIDE_PDF_PCT));
    requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
  }, [persistBesidePdfPct]);

  const mccTaskPaneBesideWire =
    !!pdfBesideDockRef && !!besideHandleSlotRef && !!onMccBesidePdfLayoutChange;

  const besidePanelOpen = !!salesAgreementSourceView || !!playbookAgentBeside;

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const docked = besidePanelOpen;
    onMccBesidePdfLayoutChange!({ docked, pdfWidthPct: saBesidePdfWidthPct });
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange, besidePanelOpen, saBesidePdfWidthPct]);

  React.useLayoutEffect(() => {
    if (!mccTaskPaneBesideWire) return;
    const cb = onMccBesidePdfLayoutChange!;
    return () => {
      cb({ docked: false, pdfWidthPct: saBesidePdfWidthPctRef.current });
    };
  }, [mccTaskPaneBesideWire, onMccBesidePdfLayoutChange]);

  React.useLayoutEffect(() => {
    if (workflowScrollRequestToken <= consumedWorkflowScrollTokenRef.current) return;
    consumedWorkflowScrollTokenRef.current = workflowScrollRequestToken;
    workflowStepsScrollAnchorRef.current?.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [workflowScrollRequestToken]);

  /** Align a clause card header just under the sticky progress/tabs row (open or closed beside layout). */
  const scrollFocusedClauseHeaderIntoView = React.useCallback(
    (clauseId: string) => {
      const headerEl = clauseCardHeaderRefs.current.get(clauseId);
      if (!headerEl) return false;

      const scrollRoot = besidePanelOpen
        ? (besideRightScrollRef?.current ??
          resultsMainColumnRef.current ??
          findScrollableAncestor(headerEl))
        : (findScrollableAncestor(headerEl) ?? resultsMainColumnRef.current);
      if (!scrollRoot) return false;

      const stickyEl = stickyHeaderRef.current;
      const anchorBottom = stickyEl
        ? stickyEl.getBoundingClientRect().bottom
        : scrollRoot.getBoundingClientRect().top;
      const headerTop = headerEl.getBoundingClientRect().top;
      const delta = headerTop - anchorBottom;
      if (Math.abs(delta) < 1) return true;
      scrollRoot.scrollTop += delta;
      return true;
    },
    [besideRightScrollRef, besidePanelOpen],
  );

  const scheduleScrollClauseHeaderIntoView = React.useCallback(
    (clauseId: string) => {
      const run = () => scrollFocusedClauseHeaderIntoView(clauseId);
      run();
      requestAnimationFrame(() => {
        run();
        requestAnimationFrame(run);
      });
    },
    [scrollFocusedClauseHeaderIntoView],
  );

  const scheduleScrollActiveBesideClauseIntoView = React.useCallback(() => {
    const clauseId =
      playbookAgentBeside?.clauseId ?? salesAgreementSourceView?.clauseId ?? null;
    if (!clauseId || !besidePanelOpen) return;
    scheduleScrollClauseHeaderIntoView(clauseId);
  }, [
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    scheduleScrollClauseHeaderIntoView,
  ]);

  React.useEffect(() => {
    scheduleScrollActiveBesideClauseRef.current = scheduleScrollActiveBesideClauseIntoView;
  }, [scheduleScrollActiveBesideClauseIntoView]);

  type BesideCloseOptions = { alignClause?: boolean };

  const handleCloseSalesAgreementSource = React.useCallback(
    (opts?: BesideCloseOptions) => {
      if (opts?.alignClause) {
        const clauseId = salesAgreementSourceView?.clauseId;
        if (clauseId) pendingCloseClauseScrollRef.current = clauseId;
      }
      salesAgreementSourceTargetRef.current = null;
      setSalesAgreementSourceView(null);
    },
    [salesAgreementSourceView?.clauseId],
  );

  const handleClosePlaybookAgent = React.useCallback(
    (opts?: BesideCloseOptions) => {
      if (opts?.alignClause) {
        const clauseId = playbookAgentBeside?.clauseId;
        if (clauseId) pendingCloseClauseScrollRef.current = clauseId;
      }
      playbookAgentLoadRef.current += 1;
      setPlaybookAgentBeside(null);
    },
    [playbookAgentBeside?.clauseId],
  );

  const handleOpenPlaybookAgent = React.useCallback(
    async (clause: CrossCheckClause) => {
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!resolvedProjectId) {
        setActionBanner({
          type: 'error',
          message: 'Project is not linked — cannot load playbook for Ask Agent.',
        });
        return;
      }

      handleCloseSalesAgreementSource();

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;
      const askDomain: AskAgentDomain =
        detectedDomain === 'construction-loan' ? 'construction-loan' : 'real-estate';
      const projectLabel =
        parentProjectName ||
        (askDomain === 'construction-loan' ? 'Past negotiations' : 'Negotiation archive');
      const workflowHint =
        askDomain === 'construction-loan' ? ('construction_loan' as const) : ('real_estate' as const);

      const loadId = playbookAgentLoadRef.current + 1;
      playbookAgentLoadRef.current = loadId;

      const sessionKey = `${clause.id}-${Date.now()}`;
      const hasGrounding = clauseHasAskAgentGrounding(clause, askDomain);
      const needsContextSourceChoice = clauseNeedsAskAgentContextSourceChoice(clause);
      const askLanguage = detectAskAgentLanguage(clause);
      const initialQuestion =
        hasGrounding && !needsContextSourceChoice
          ? getAskAgentInitialQuestion(askDomain, askLanguage)
          : null;

      setPlaybookAgentBeside({
        sessionKey,
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: true,
        error: null,
        contextBody: '',
        projectLabel,
        recordCount: 0,
        playbookRows: [],
        matchRankToArchiveRecordNum: new Map(),
        playbookBodyCached: '',
        askDomain,
        askLanguage,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingDeepResearchConsent: false,
        awaitingContextSourceChoice: needsContextSourceChoice,
        contextSource: null,
      });

      try {
        const rows = await apiService.listPlaybooksForAgent(resolvedProjectId, workflowHint);
        if (playbookAgentLoadRef.current !== loadId) return;

        const playbookBody = rows.length
          ? buildPlaybookContextFromRows(projectLabel, rows)
          : '(no playbook archive records for this project)';

        const contextSourceForBuild: AskAgentContextSource | null = needsContextSourceChoice
          ? null
          : 'both';
        const matchRankToArchiveRecordNum = buildMatchRankToArchiveRecordNum(
          clause.past_agreement?.options ?? [],
          buildPlaybookRecordNumberById(rows),
        );
        const clausePrefix = buildClauseAskAgentPrefix(
          clause,
          playbookPlainTextById,
          playbookFileNameById,
          askDomain,
          contextSourceForBuild,
          rows,
        );
        const includePlaybook = !needsContextSourceChoice;
        const contextBody = combineAskAgentContext(clausePrefix, playbookBody, {
          includePlaybookArchive: includePlaybook,
          contextSource: needsContextSourceChoice ? null : 'both',
        });

        if (!rows.length && !clausePrefix.trim()) {
          setPlaybookAgentBeside({
            sessionKey,
            clauseId: clause.id,
            clauseNumberDisplay,
            loading: false,
            error: 'No clause context or playbook records available for Ask Agent.',
            contextBody: '',
            projectLabel,
            recordCount: 0,
            playbookRows: [],
            matchRankToArchiveRecordNum: new Map(),
            playbookBodyCached: playbookBody,
            askDomain,
            askLanguage,
            initialQuestion: null,
            openingAssistantMessage: null,
            awaitingDeepResearchConsent: false,
            awaitingContextSourceChoice: false,
            contextSource: null,
          });
          return;
        }

        const askCopy = getAskAgentCopy(askLanguage);
        const openingAssistantMessage = needsContextSourceChoice
          ? askCopy.contextSourceQuestion
          : !hasGrounding
            ? rows.length > 0
              ? getAskAgentNoMatchesNotice(askDomain, askLanguage)
              : getAskAgentNoMatchesNoPlaybookNotice(askDomain, askLanguage)
            : null;

        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: null,
          contextBody,
          projectLabel,
          recordCount: rows.length,
          playbookRows: rows,
          matchRankToArchiveRecordNum,
          playbookBodyCached: playbookBody,
          askDomain,
          askLanguage,
          initialQuestion,
          openingAssistantMessage,
          awaitingDeepResearchConsent: !hasGrounding && !needsContextSourceChoice && rows.length > 0,
          awaitingContextSourceChoice: needsContextSourceChoice,
          contextSource: null,
        });
      } catch (err: unknown) {
        if (playbookAgentLoadRef.current !== loadId) return;
        const msg =
          err instanceof Error ? err.message : 'Failed to load playbook for Ask Agent';
        setPlaybookAgentBeside({
          sessionKey,
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: msg,
          contextBody: '',
          projectLabel,
          recordCount: 0,
          playbookRows: [],
          matchRankToArchiveRecordNum: new Map(),
          playbookBodyCached: '',
          askDomain,
          askLanguage,
          initialQuestion: null,
          openingAssistantMessage: null,
          awaitingDeepResearchConsent: false,
          awaitingContextSourceChoice: false,
          contextSource: null,
        });
      }
    },
    [
      projectId,
      projectIdProp,
      parentProjectName,
      playbookPlainTextById,
      playbookFileNameById,
      detectedDomain,
      handleCloseSalesAgreementSource,
    ],
  );

  const handleChooseAskAgentContextSource = React.useCallback(
    (source: AskAgentContextSource) => {
      const agent = playbookAgentBeside;
      if (!agent?.awaitingContextSourceChoice) return;

      const clause = clauses.find((c) => c.id === agent.clauseId);
      if (!clause) return;

      const clausePrefix = buildClauseAskAgentPrefix(
        clause,
        playbookPlainTextById,
        playbookFileNameById,
        agent.askDomain,
        source,
        agent.playbookRows,
      );
      const includePlaybook =
        source === 'past_negotiations' || source === 'both';
      const contextBody = combineAskAgentContext(clausePrefix, agent.playbookBodyCached, {
        includePlaybookArchive: includePlaybook,
        contextSource: source,
      });
      const initialQuestion = getAskAgentInitialQuestionForSource(
        source,
        agent.askDomain,
        agent.askLanguage,
      );

      setPlaybookAgentBeside({
        ...agent,
        loading: false,
        error: null,
        contextBody,
        initialQuestion,
        openingAssistantMessage: null,
        awaitingContextSourceChoice: false,
        contextSource: source,
        awaitingDeepResearchConsent: false,
      });
    },
    [playbookAgentBeside, clauses, playbookPlainTextById, playbookFileNameById],
  );

  const handleApplyAgentSuggestedEdit = React.useCallback(
    async (chatMessages: PlaybookAgentChatTurn[]) => {
      const agentState = playbookAgentBeside;
      const resolvedProjectId = projectId ?? projectIdProp;
      if (!agentState?.clauseId || !resolvedProjectId) {
        throw new Error('Project or clause is not available');
      }
      const clause = clauses.find((c) => c.id === agentState.clauseId);
      if (!clause) {
        throw new Error('Clause not found');
      }

      setAgentAiStreamingClauseId(clause.id);
      try {
        const suggestedXml = await streamAiSuggestionFromChat(
          resolvedProjectId,
          clause,
          chatMessages,
          {
            contextSource: agentState.contextSource,
            askDomain: agentState.askDomain,
            askLanguage: agentState.askLanguage,
            playbookPlainTextById,
            playbookFileNameById,
          },
        );
        if (!suggestedXml) {
          throw new Error('No suggestion was generated');
        }
        setAgentSuggestedXmlByClauseId((prev) => ({ ...prev, [clause.id]: suggestedXml }));
      } finally {
        setAgentAiStreamingClauseId(null);
      }
    },
    [
      playbookAgentBeside,
      projectId,
      projectIdProp,
      clauses,
      playbookPlainTextById,
      playbookFileNameById,
    ],
  );

  const handleOpenSalesAgreementSource = React.useCallback(
    async (clause: CrossCheckClause, searchTextOverride?: string) => {
      const text = (searchTextOverride ?? clause.sales_agreement ?? '')
        .replace(/\s+/g, ' ')
        .trim();
      if (!subProjectId || !text) return;

      // Toggle off when the badge is clicked on the currently-active clause.
      if (
        salesAgreementSourceView &&
        salesAgreementSourceView.clauseId === clause.id &&
        !salesAgreementSourceView.loading
      ) {
        handleCloseSalesAgreementSource({ alignClause: true });
        return;
      }

      const clauseNumberDisplay =
        clause.clause_number != null
          ? formatClauseNumberForDisplay(clause.clause_number)
          : null;

      handleClosePlaybookAgent();

      salesAgreementSourceTargetRef.current = clause.id;
      setSalesAgreementSourceView({
        clauseId: clause.id,
        clauseNumberDisplay,
        loading: true,
        error: null,
        buffer: null,
        page: 1,
        total: 1,
        searchText: text,
      });

      try {
        const { blob, highlightPage, totalPages } =
          await apiService.getSalesAgreementSourcePdf(
            subProjectId,
            text,
            detectedDomain ?? 'real-estate',
          );
        const buffer = await blob.arrayBuffer();
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: null,
          buffer,
          page: highlightPage,
          total: totalPages,
          searchText: text,
        });
      } catch (err: any) {
        if (salesAgreementSourceTargetRef.current !== clause.id) return;
        const detail =
          err?.response?.data?.detail ||
          (typeof err?.message === 'string' ? err.message : null) ||
          'Failed to load Sales Agreement source PDF';
        setSalesAgreementSourceView({
          clauseId: clause.id,
          clauseNumberDisplay,
          loading: false,
          error: typeof detail === 'string' ? detail : 'Failed to load Sales Agreement source PDF',
          buffer: null,
          page: 1,
          total: 1,
          searchText: text,
        });
      }
    },
    [
      subProjectId,
      detectedDomain,
      salesAgreementSourceView,
      handleCloseSalesAgreementSource,
      handleClosePlaybookAgent,
    ],
  );

  // If the user switches tab while the split panel is open, close it — the
  // panel is bound to a specific clause that may not be in the new tab.
  React.useEffect(() => {
    if (!salesAgreementSourceView && !playbookAgentBeside) return;
    handleCloseSalesAgreementSource();
    handleClosePlaybookAgent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  React.useEffect(() => {
    loadResults();
  }, [loadResults]);

  // Keep a ref that always holds the current workflowStatus so the interval
  // callback can read the latest value without being re-created each render.
  const workflowStatusRef = React.useRef(workflowStatus);
  React.useEffect(() => {
    workflowStatusRef.current = workflowStatus;
  }, [workflowStatus]);

  // Poll while the cross-check is active.
  //
  // Design note: we intentionally do NOT stop immediately when the initial
  // status is "completed" — that status may be stale from a previous run.
  // The MCC / Construction-Loan flow calls onComplete() (which mounts this
  // component) *before* autoStartCrossCheck fires, so the backend can still
  // show the old "completed" state for several seconds while the new run is
  // being prepared.  We therefore let the interval run and decide to stop
  // only after:
  //   a) we have observed a non-terminal status followed by a terminal one
  //      (genuine completion of the new run), OR
  //   b) we have seen 3 consecutive terminal polls without any non-terminal
  //      in between (old, already-finished sub-project — stop to avoid
  //      hammering the API).
  React.useEffect(() => {
    if (!subProjectId) return;

    // Base cadence + safety caps. A failing poll must never run forever: on auth
    // failure we stop immediately (re-auth required), and on transient errors we
    // back off exponentially and give up after a few consecutive failures. This
    // is the client-side guard against the 2026-06-22 arnon-tl tokenless 401 storm.
    const BASE_INTERVAL_MS = 3000;
    const MAX_BACKOFF_MS = 60000;
    const MAX_CONSECUTIVE_ERRORS = 5;

    let stopped = false;
    let hasSeenNonTerminal = false;
    let consecutiveTerminalPolls = 0;
    let consecutiveErrors = 0;
    let timeoutId: number | undefined;

    const schedule = (delayMs: number) => {
      if (stopped) return;
      timeoutId = window.setTimeout(poll, delayMs);
    };

    const poll = async () => {
      if (stopped) return;
      const result = await loadResults(true);
      if (stopped) return;

      // Auth failure: token can't be silently refreshed → stop and let the user
      // re-authenticate. Continuing would spam the API with tokenless requests.
      if (result.authError) {
        log('CrossCheckResultsView: polling stopped (auth failure — re-auth required)');
        stopped = true;
        return;
      }

      // Transient failure: back off, and bail out after too many in a row.
      if (!result.ok) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          log('CrossCheckResultsView: polling stopped (too many consecutive errors)', {
            consecutiveErrors,
          });
          stopped = true;
          setPollingError(false);
          setPollingStopped(true);
          return;
        }
        const backoff = Math.min(BASE_INTERVAL_MS * 2 ** consecutiveErrors, MAX_BACKOFF_MS);
        schedule(backoff);
        return;
      }

      consecutiveErrors = 0;
      setPollingError(false);
      setPollingStopped(false);

      const s = (workflowStatusRef.current || '').toLowerCase();
      const isTerminal =
        s === 'error' || s === 'failed' || s === 'complete' || s === 'completed';

      if (!isTerminal) {
        hasSeenNonTerminal = true;
        consecutiveTerminalPolls = 0;
      } else {
        consecutiveTerminalPolls += 1;
        if (hasSeenNonTerminal || consecutiveTerminalPolls >= 3) {
          stopped = true;
          return;
        }
      }

      schedule(BASE_INTERVAL_MS);
    };

    schedule(BASE_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [subProjectId, loadResults, pollRetryKey]);

  const stepStatuses = React.useMemo((): { step0: StepStatus; step1: StepStatus; step2: StepStatus } => {
    // Avoid the default "step 1 running" before we have server state (and during explicit reload).
    if (loading) {
      return { step0: 'complete', step1: 'pending', step2: 'pending' };
    }

    const hasCrossCheckResults = clauses.some((c) => {
      if (Boolean(c.sales_agreement) || Boolean(c.bank_of_changes)) return true;
      if (Array.isArray(c.insights) && c.insights.length > 0) return true;
      const past = c.past_agreement?.options;
      if (Array.isArray(past) && past.length > 0) return true;
      return false;
    });

    const wp = workflowProgressBlob;
    const stepKey = String(wp?.step ?? '').toLowerCase();
    const isActive = wp?.is_active === true;
    const progNum =
      typeof wp?.progress === 'number'
        ? wp.progress
        : typeof wp?.progress_percentage === 'number'
          ? (wp.progress_percentage as number)
          : null;

    const normalizedStatus = (workflowStatus || '').toLowerCase();
    const workflowComplete =
      normalizedStatus === 'complete' ||
      normalizedStatus === 'completed' ||
      stepKey === 'step3_completed' ||
      (stepKey === 'complete' && !isActive) ||
      (workflowStep === 3 &&
        !isActive &&
        (progNum == null || progNum >= 100) &&
        normalizedStatus !== 'running');
    const workflowError = normalizedStatus === 'error' || normalizedStatus === 'failed';
    const workflowStarted =
      Boolean(workflowProgressBlob && Object.keys(workflowProgressBlob).length > 0) ||
      workflowStep != null ||
      isActive ||
      ['running', 'in_progress', 'complete', 'completed', 'error', 'failed'].includes(normalizedStatus);

    // step0 (Identify sides) is always complete — it finished before we arrive here
    const step0: StepStatus = 'complete';
    let step1: StepStatus = 'running'; // default once workflow has started
    let step2: StepStatus = 'pending';

    if (!workflowStarted) {
      return { step0, step1: 'pending', step2: 'pending' };
    }

    if (workflowError) {
      step2 = 'error';
      step1 = workflowStep && workflowStep >= 2 ? 'complete' : 'running';
    } else if (workflowComplete || hasCrossCheckResults) {
      step1 = 'complete';
      step2 = 'complete';
    } else if (workflowStep && workflowStep >= 3) {
      step1 = 'complete';
      step2 = 'running';
    } else if (workflowStep && workflowStep >= 2) {
      step1 = 'complete';
      step2 = 'running';
    } else if (clauses.length > 0) {
      step1 = 'complete';
      step2 = 'running';
    }
    // workflowStep === 1 or null with no cards → step1 stays 'running'

    return { step0, step1, step2 };
  }, [workflowStep, workflowStatus, workflowProgressBlob, clauses, loading]);

  // Per-step timers — always running so the clock is live from mount
  React.useEffect(() => {
    const id = setInterval(() => setTimerNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  React.useEffect(() => {
    const now = Date.now();
    const t = stepTimingRef.current;

    const applyStatus = (
      status: StepStatus,
      startKey: 'step1StartMs' | 'step2StartMs',
      elapsedKey: 'step1ElapsedSec' | 'step2ElapsedSec',
      fallbackStartMs?: number
    ) => {
      if (status === 'pending') {
        // Don't reset start if already ticking — keeps timer live if status briefly flickers
        if (!t[startKey]) t[elapsedKey] = 0;
        return;
      }
      if (status === 'running') {
        // Seed from fallback (mount time) if we've never seen this step start
        if (!t[startKey]) t[startKey] = fallbackStartMs ?? now;
        return;
      }
      // complete / error — freeze the elapsed time
      if (t[startKey]) {
        t[elapsedKey] = Math.max(1, Math.floor((now - t[startKey]!) / 1000));
        t[startKey] = null;
      }
    };

    applyStatus(stepStatuses.step1, 'step1StartMs', 'step1ElapsedSec', mountMsRef.current);
    applyStatus(stepStatuses.step2, 'step2StartMs', 'step2ElapsedSec');
  }, [stepStatuses.step1, stepStatuses.step2]);

  const formatStepSeconds = React.useCallback((totalSec: number): string => {
    const sec = Math.max(0, Math.floor(totalSec));
    const mm = String(Math.floor(sec / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, []);

  const stepTimes = React.useMemo(() => {
    const t = stepTimingRef.current;
    const now = timerNowMs;
    const step1Sec =
      t.step1StartMs != null
        ? t.step1ElapsedSec + Math.floor((now - t.step1StartMs) / 1000)
        : t.step1ElapsedSec;
    const step2Sec =
      t.step2StartMs != null
        ? t.step2ElapsedSec + Math.floor((now - t.step2StartMs) / 1000)
        : t.step2ElapsedSec;
    return {
      step1: formatStepSeconds(step1Sec),
      step2: formatStepSeconds(step2Sec),
    };
  }, [timerNowMs, formatStepSeconds]);

  const hasConstructionLoanMatter = React.useMemo(() => (
    clauses.some((c) => {
      const matter = (c as any).matter;
      return matter === 'commercial' || matter === 'legal' || matter === 'other';
    })
  ), [clauses]);

  const isConstructionLoan = detectedDomain === 'construction-loan' || hasConstructionLoanMatter;

  // Treat any clause whose `matter` is not one of the three known matter
  // values as 'other' for tab grouping purposes (handles legacy records that
  // were classified before the matter taxonomy existed).
  const matterFor = React.useCallback((c: CrossCheckClause): MatterTabId => {
    const raw = (c as any).matter;
    if (raw === 'commercial' || raw === 'legal' || raw === 'other') {
      return raw;
    }
    return 'other';
  }, []);

  const filteredClauses = React.useMemo(() => {
    if (activeTab === 'all') return clauses;
    if (isConstructionLoan) {
      return clauses.filter((c) => matterFor(c) === activeTab);
    }
    if (activeTab === 'saved') return clauses.filter((c) => c.documentType === 'saved_for_later');
    return clauses.filter((c) => c.documentType === (activeTab as CrossCheckDocumentType));
  }, [clauses, activeTab, isConstructionLoan, matterFor]);

  const showClauseSubFilters = React.useMemo(() => {
    if (isConstructionLoan) return true;
    return clauses.some((c) => (c.past_agreement?.options?.length ?? 0) > 0);
  }, [isConstructionLoan, clauses]);

  const hasActiveClauseSubFilters = filterFinalVersion || filterMinSimilarity != null;
  const hasActiveSubFilters =
    hasActiveClauseSubFilters || filterSelectedAuthors !== null || filterSelectedYears !== null;

  const availableAuthors = React.useMemo(
    () => collectPastNegotiationAuthors(filteredClauses, playbookAuthorById),
    [filteredClauses, playbookAuthorById],
  );

  const availableYears = React.useMemo(
    () => collectPastNegotiationYears(filteredClauses, playbookTimestampById),
    [filteredClauses, playbookTimestampById],
  );

  const authorFilterLabel = React.useMemo(
    () => formatAuthorFilterLabel(filterSelectedAuthors),
    [filterSelectedAuthors],
  );

  const timeFilterLabel = React.useMemo(
    () => formatTimeFilterLabel(filterSelectedYears),
    [filterSelectedYears],
  );

  const displayedClauses = React.useMemo(() => {
    if (!hasActiveClauseSubFilters) return filteredClauses;
    return applyClauseEditorFilters(filteredClauses, {
      finalVersion: filterFinalVersion,
      minSimilarity: filterMinSimilarity,
      finalVersionById: playbookFinalVersionById,
    });
  }, [
    filteredClauses,
    filterFinalVersion,
    filterMinSimilarity,
    hasActiveClauseSubFilters,
    playbookFinalVersionById,
  ]);

  const closableCount = React.useMemo(
    () => displayedClauses.filter(isClauseClosable).length,
    [displayedClauses],
  );

  const openableCount = React.useMemo(
    () => displayedClauses.filter((clause) => clause.lock_status?.locked).length,
    [displayedClauses],
  );

  const performBulkSetLock = React.useCallback(
    async (clausesToUpdate: CrossCheckClause[], locked: boolean): Promise<number> => {
      if (!subProjectId || !clausesToUpdate.length) return 0;

      const clauseIds = clausesToUpdate.map((clause) => clause.id).filter(Boolean) as string[];
      if (!clauseIds.length) return 0;

      const response = await apiService.setClausesLock(subProjectId, clauseIds, locked);
      const updatedIds = new Set(response?.data?.clause_ids ?? []);

      if (updatedIds.size > 0) {
        setClauses((prev) =>
          prev.map((c) =>
            updatedIds.has(c.id)
              ? { ...c, lock_status: { ...(c.lock_status || {}), locked } }
              : c
          )
        );
      }

      return updatedIds.size;
    },
    [subProjectId],
  );

  const performBulkLock = React.useCallback(
    async (clausesToLock: CrossCheckClause[]): Promise<number> =>
      performBulkSetLock(clausesToLock, true),
    [performBulkSetLock],
  );

  const handleOpenAllFiltered = React.useCallback(() => {
    if (!subProjectId || bulkOpening || bulkClosing) return;
    const toOpen = displayedClauses.filter((clause) => clause.lock_status?.locked);
    if (!toOpen.length) return;

    void (async () => {
      setBulkOpening(true);
      setActionBanner({
        type: 'loading',
        message: `Opening ${toOpen.length} clause${toOpen.length === 1 ? '' : 's'}…`,
      });
      try {
        const openedCount = await performBulkSetLock(toOpen, false);
        if (openedCount === toOpen.length) {
          setActionBanner({
            type: 'success',
            message: `Opened ${openedCount} clause${openedCount === 1 ? '' : 's'}.`,
          });
        } else if (openedCount > 0) {
          setActionBanner({
            type: 'error',
            message: `Opened ${openedCount} of ${toOpen.length} clauses. Some could not be unlocked.`,
          });
        } else {
          setActionBanner({
            type: 'error',
            message: 'Failed to open the selected clauses.',
          });
        }
      } catch (err: any) {
        console.error('[CrossCheckResultsView] Bulk open failed:', err);
        setActionBanner({
          type: 'error',
          message: 'Failed to open the selected clauses.',
        });
      } finally {
        setBulkOpening(false);
      }
    })();
  }, [subProjectId, bulkOpening, bulkClosing, displayedClauses, performBulkSetLock]);

  const handleCloseFiltered = React.useCallback(() => {
    if (!subProjectId || bulkClosing || bulkOpening || !hasActiveClauseSubFilters) return;
    const toLock = displayedClauses.filter(isClauseClosable);
    if (!toLock.length) return;

    const { withTrackChanges, withoutTrackChanges } = partitionClosableByTrackChanges(toLock);
    if (withTrackChanges.length > 0) {
      setBulkClosePrompt({ toLock, withTrackChanges, withoutTrackChanges });
      return;
    }

    void (async () => {
      setBulkClosing(true);
      setActionBanner({
        type: 'loading',
        message: `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
      });
      try {
        const lockedCount = await performBulkLock(toLock);
        if (lockedCount === toLock.length) {
          setActionBanner({
            type: 'success',
            message: `Closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'}.`,
          });
        } else if (lockedCount > 0) {
          setActionBanner({
            type: 'error',
            message: `Closed ${lockedCount} of ${toLock.length} clauses. Some could not be locked.`,
          });
        } else {
          setActionBanner({
            type: 'error',
            message: 'Failed to close the selected clauses.',
          });
        }
      } catch (err: any) {
        console.error('[CrossCheckResultsView] Bulk close failed:', err);
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses.',
        });
      } finally {
        setBulkClosing(false);
      }
    })();
  }, [subProjectId, bulkClosing, bulkOpening, hasActiveClauseSubFilters, displayedClauses, performBulkLock]);

  const handleBulkCloseWithoutAccept = React.useCallback(async () => {
    if (!bulkClosePrompt || bulkClosing) return;
    const { toLock } = bulkClosePrompt;
    if (!toLock.length) {
      setBulkClosePrompt(null);
      return;
    }

    setBulkClosing(true);
    setBulkCloseBusyMessage(
      `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
    );
    try {
      const lockedCount = await performBulkLock(toLock);
      if (lockedCount === toLock.length) {
        setActionBanner({
          type: 'success',
          message: `Closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'} without accepting track changes.`,
        });
      } else if (lockedCount > 0) {
        setActionBanner({
          type: 'error',
          message: `Closed ${lockedCount} of ${toLock.length} clauses. Some could not be locked.`,
        });
      } else {
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses.',
        });
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Bulk close without accept failed:', err);
      setActionBanner({
        type: 'error',
        message: 'Failed to close the selected clauses.',
      });
    } finally {
      setBulkClosing(false);
      setBulkCloseBusyMessage(null);
      setBulkClosePrompt(null);
    }
  }, [bulkClosePrompt, bulkClosing, performBulkLock]);

  const handleBulkCloseAcceptAndClose = React.useCallback(async () => {
    if (!subProjectId || !bulkClosePrompt || bulkClosing) return;
    const { toLock, withTrackChanges } = bulkClosePrompt;

    setBulkClosing(true);
    setBulkCloseBusyMessage(
      `Accepting track changes for ${withTrackChanges.length} clause${withTrackChanges.length === 1 ? '' : 's'}…`,
    );

    let acceptedCount = 0;
    try {
      const acceptedIds = await acceptClausesSequentially(
        subProjectId,
        withTrackChanges,
        (current, total) => {
          setBulkCloseBusyMessage(`Accepting track changes (${current}/${total})…`);
        },
      );
      acceptedCount = acceptedIds.length;
      for (const clauseId of acceptedIds) {
        handleResponseSubmitted(clauseId);
      }

      setBulkCloseBusyMessage(
        `Closing ${toLock.length} clause${toLock.length === 1 ? '' : 's'}…`,
      );
      const lockedCount = await performBulkLock(toLock);

      if (lockedCount === toLock.length) {
        const acceptNote =
          acceptedCount < withTrackChanges.length
            ? ` Accepted ${acceptedCount} of ${withTrackChanges.length} track-change clause${withTrackChanges.length === 1 ? '' : 's'}.`
            : '';
        setActionBanner({
          type: 'success',
          message: `Accepted changes and closed ${lockedCount} clause${lockedCount === 1 ? '' : 's'}.${acceptNote}`,
        });
      } else if (lockedCount > 0) {
        setActionBanner({
          type: 'error',
          message: `Closed ${lockedCount} of ${toLock.length} clauses after accepting track changes.`,
        });
      } else {
        setActionBanner({
          type: 'error',
          message: 'Failed to close the selected clauses after accepting track changes.',
        });
      }
    } catch (err: any) {
      console.error('[CrossCheckResultsView] Bulk accept and close failed:', err);
      setActionBanner({
        type: 'error',
        message: 'Failed to accept track changes and close the selected clauses.',
      });
    } finally {
      setBulkClosing(false);
      setBulkCloseBusyMessage(null);
      setBulkClosePrompt(null);
    }
  }, [
    subProjectId,
    bulkClosePrompt,
    bulkClosing,
    performBulkLock,
    handleResponseSubmitted,
  ]);

  const applySimilarityThreshold = React.useCallback((threshold: number) => {
    const clamped = Math.min(SIMILARITY_FILTER_MAX, Math.max(SIMILARITY_FILTER_MIN, threshold));
    setSimilaritySliderValue(clamped);
    setFilterMinSimilarity(clamped);
  }, []);

  const handleSimilarityChipClick = React.useCallback(() => {
    setSimilarityPanelOpen((open) => {
      const nextOpen = !open;
      if (nextOpen) {
        const nextThreshold = filterMinSimilarity ?? SIMILARITY_FILTER_MAX;
        setSimilaritySliderValue(nextThreshold);
        setFilterMinSimilarity(nextThreshold);
      }
      return nextOpen;
    });
  }, [filterMinSimilarity]);

  const handleSimilaritySliderChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      applySimilarityThreshold(Number(event.target.value));
    },
    [applySimilarityThreshold],
  );

  const handleClearSubFilters = React.useCallback(() => {
    setFilterFinalVersion(false);
    setFilterMinSimilarity(null);
    setSimilarityPanelOpen(false);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    setFilterSelectedAuthors(null);
    setAuthorsPanelOpen(false);
    setFilterSelectedYears(null);
    setTimePanelOpen(false);
  }, []);

  const handleAuthorsChipClick = React.useCallback(() => {
    setAuthorsPanelOpen((open) => !open);
    setTimePanelOpen(false);
  }, []);

  const handleTimeChipClick = React.useCallback(() => {
    setTimePanelOpen((open) => !open);
    setAuthorsPanelOpen(false);
  }, []);

  const handleSelectAllTime = React.useCallback(() => {
    setFilterSelectedYears(null);
  }, []);

  const handleToggleYearFilter = React.useCallback(
    (year: number) => {
      setFilterSelectedYears((prev) => {
        if (prev === null) {
          const next = new Set(availableYears.filter((y) => y !== year));
          return next.size === 0 ? new Set() : next;
        }
        const next = new Set(prev);
        if (next.has(year)) next.delete(year);
        else next.add(year);
        if (next.size === 0) return new Set();
        if (next.size >= availableYears.length) return null;
        return next;
      });
    },
    [availableYears],
  );

  const handleClearTimeFilter = React.useCallback(() => {
    setFilterSelectedYears(null);
    setTimePanelOpen(false);
  }, []);

  const handleSelectAllAuthors = React.useCallback(() => {
    setFilterSelectedAuthors(null);
  }, []);

  const handleToggleAuthorFilter = React.useCallback(
    (author: string) => {
      setFilterSelectedAuthors((prev) => {
        if (prev === null) {
          const next = new Set(availableAuthors.filter((name) => name !== author));
          return next.size === 0 ? new Set() : next;
        }
        const next = new Set(prev);
        if (next.has(author)) next.delete(author);
        else next.add(author);
        if (next.size === 0) return new Set();
        if (next.size >= availableAuthors.length) return null;
        return next;
      });
    },
    [availableAuthors],
  );

  const handleClearAuthorsFilter = React.useCallback(() => {
    setFilterSelectedAuthors(null);
    setAuthorsPanelOpen(false);
  }, []);

  const handleClearSimilarityFilter = React.useCallback(() => {
    setFilterMinSimilarity(null);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    setSimilarityPanelOpen(false);
  }, []);

  React.useEffect(() => {
    if (!similarityPanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = similarityFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setSimilarityPanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [similarityPanelOpen]);

  React.useEffect(() => {
    if (!authorsPanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = authorsFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setAuthorsPanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [authorsPanelOpen]);

  React.useEffect(() => {
    if (!timePanelOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const root = timeFilterRef.current;
      if (!root || root.contains(event.target as Node)) return;
      setTimePanelOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [timePanelOpen]);

  React.useEffect(() => {
    if (filterSelectedYears === null) return;
    const stale = Array.from(filterSelectedYears).some((year) => !availableYears.includes(year));
    if (!stale) return;
    const next = new Set(
      Array.from(filterSelectedYears).filter((year) => availableYears.includes(year)),
    );
    setFilterSelectedYears(next.size === 0 || next.size >= availableYears.length ? null : next);
  }, [availableYears, filterSelectedYears]);

  React.useEffect(() => {
    if (filterSelectedAuthors === null) return;
    const stale = Array.from(filterSelectedAuthors).some((author) => !availableAuthors.includes(author));
    if (stale) {
      const next = new Set(
        Array.from(filterSelectedAuthors).filter((author) => availableAuthors.includes(author)),
      );
      setFilterSelectedAuthors(next.size === 0 || next.size >= availableAuthors.length ? null : next);
    }
  }, [availableAuthors, filterSelectedAuthors]);

  const counts = React.useMemo(() => ({
    all: clauses.length,
    sales_agreement: clauses.filter((c) => c.documentType === 'sales_agreement').length,
    appendix_a: clauses.filter((c) => c.documentType === 'appendix_a').length,
    saved: clauses.filter((c) => c.documentType === 'saved_for_later').length,
    commercial: clauses.filter((c) => matterFor(c) === 'commercial').length,
    legal: clauses.filter((c) => matterFor(c) === 'legal').length,
    other: clauses.filter((c) => matterFor(c) === 'other').length,
  }), [clauses, matterFor]);

  // Progress = locked OR submitted to response OR submitted to schedule (same logic as courtyard dashboard)
  const progress = React.useMemo(() => {
    const total = clauses.length;
    const current = clauses.filter((c) => {
      const isLocked = c.lock_status?.locked ?? false;
      const isSubmittedToResponse = c.submission_status?.response_submitted ?? false;
      const isSubmittedToSchedule = c.submission_status?.schedule_submitted ?? false;
      return isLocked || isSubmittedToResponse || isSubmittedToSchedule;
    }).length;
    const pct = total ? Math.round((current / total) * 100) : 0;
    return { current, total, pct };
  }, [clauses]);

  // Construction Loan classifies each request by `matter` (commercial /
  // legal / other) instead of by document type, so the tabs differ.
  const tabs: { id: TabId; label: string; cssClass: string }[] = isConstructionLoan
    ? [
        { id: 'all', label: `All (${counts.all})`, cssClass: 'cc-tab-all' },
        { id: 'commercial', label: `Commercial (${counts.commercial})`, cssClass: 'cc-tab-sales' },
        { id: 'legal', label: `Legal (${counts.legal})`, cssClass: 'cc-tab-appendix' },
        { id: 'other', label: `Other (${counts.other})`, cssClass: 'cc-tab-saved' },
      ]
    : [
        { id: 'all', label: `All (${counts.all})`, cssClass: 'cc-tab-all' },
        { id: 'sales_agreement', label: `Sales Agreement (${counts.sales_agreement})`, cssClass: 'cc-tab-sales' },
        { id: 'appendix_a', label: `Appendix A (${counts.appendix_a})`, cssClass: 'cc-tab-appendix' },
        { id: 'saved', label: `Saved (${counts.saved})`, cssClass: 'cc-tab-saved' },
      ];

  // If the detected domain switches (e.g. CL → MCC) reset the tab so we don't
  // end up sticking on a tab id that no longer exists in the new tab set.
  React.useEffect(() => {
    const allowed = new Set<TabId>(tabs.map((t) => t.id));
    if (!allowed.has(activeTab)) {
      setActiveTab('all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConstructionLoan]);

  // When tab changes, scroll outer task pane so progress + tabs stay in view (single scroll: .clauses-view)
  React.useEffect(() => {
    const prev = prevActiveTabForScrollRef.current;
    if (prev === null) {
      prevActiveTabForScrollRef.current = activeTab;
      return;
    }
    if (prev === activeTab) {
      return;
    }
    prevActiveTabForScrollRef.current = activeTab;
    setFilterFinalVersion(false);
    setFilterMinSimilarity(null);
    setSimilarityPanelOpen(false);
    setSimilaritySliderValue(SIMILARITY_FILTER_MAX);
    stickyHeaderRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [activeTab]);

  const handleTabClick = React.useCallback((tabId: TabId) => {
    setActiveTab(tabId);
  }, []);

  const useTaskPaneBesidePortalLayout =
    mccTaskPaneBesideWire && !!besidePdfChromeInDock && besidePanelOpen;
  const pdfBesideDockHost = pdfBesideDockRef?.current ?? null;
  const besideHandleSlotHost = besideHandleSlotRef?.current ?? null;
  const showBesidePortal =
    useTaskPaneBesidePortalLayout && pdfBesideDockHost != null && besideHandleSlotHost != null;

  React.useLayoutEffect(() => {
    if (besidePanelOpen) {
      prevBesidePanelOpenRef.current = true;
      scheduleScrollActiveBesideClauseIntoView();
      return;
    }

    const wasOpen = prevBesidePanelOpenRef.current;
    prevBesidePanelOpenRef.current = false;
    if (!wasOpen) return;

    const clauseId = pendingCloseClauseScrollRef.current;
    pendingCloseClauseScrollRef.current = null;
    if (!clauseId) return;

    scheduleScrollClauseHeaderIntoView(clauseId);
    const settleTimer = window.setTimeout(
      () => scheduleScrollClauseHeaderIntoView(clauseId),
      160,
    );
    return () => window.clearTimeout(settleTimer);
  }, [
    playbookAgentBeside?.clauseId,
    playbookAgentBeside?.sessionKey,
    salesAgreementSourceView?.clauseId,
    besidePanelOpen,
    besidePdfChromeInDock,
    scheduleScrollActiveBesideClauseIntoView,
    scheduleScrollClauseHeaderIntoView,
  ]);

  /** Re-align focused clause header after Word add-in or split-pane resize settles. */
  React.useEffect(() => {
    if (!besidePanelOpen) return;

    const observeTargets: HTMLElement[] = [];
    const scrollRoot = besideRightScrollRef?.current;
    if (scrollRoot) observeTargets.push(scrollRoot);
    if (resultsViewRef.current) observeTargets.push(resultsViewRef.current);
    if (besideResizeOuterRef?.current) observeTargets.push(besideResizeOuterRef.current);
    if (observeTargets.length === 0) return;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const onResizeSettled = () => {
      if (besideResizeDragRef.current) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        scheduleScrollActiveBesideClauseRef.current();
      }, 160);
    };

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(onResizeSettled);
      observeTargets.forEach((el) => ro!.observe(el));
    }

    window.addEventListener('resize', onResizeSettled);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', onResizeSettled);
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [
    besidePanelOpen,
    besideRightScrollRef,
    besideResizeOuterRef,
    playbookAgentBeside?.clauseId,
    salesAgreementSourceView?.clauseId,
    scheduleScrollActiveBesideClauseIntoView,
  ]);

  const besideResizeHandleEl = (
    <div
      className="cc-sa-beside-resize-handle"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize side panel and clause list"
      aria-valuemin={MIN_BESIDE_PDF_PCT}
      aria-valuemax={MAX_BESIDE_PDF_PCT}
      aria-valuenow={Math.round(saBesidePdfWidthPct)}
      tabIndex={0}
      title="Drag to resize. Double-click to reset. Arrow keys nudge."
      onPointerDown={handleBesideResizePointerDown}
      onPointerMove={handleBesideResizePointerMove}
      onPointerUp={endBesideResizeDrag}
      onPointerCancel={endBesideResizeDrag}
      onLostPointerCapture={() => {
        if (besideResizeDragRef.current) {
          besideResizeDragRef.current = null;
          setSaBesidePdfWidthPct((prev) => persistBesidePdfPct(prev));
          requestAnimationFrame(() => scheduleScrollActiveBesideClauseRef.current());
        }
      }}
      onKeyDown={handleBesideResizeKeyDown}
      onDoubleClick={handleBesideResizeDoubleClick}
    />
  );

  const renderBesideResizeHandlePortal = (portalTarget: HTMLElement | null) =>
    portalTarget ? createPortal(besideResizeHandleEl, portalTarget) : null;

  return (
    <div
      ref={resultsViewRef}
      className={`cc-results-view${
        besidePanelOpen ? ' cc-results-view--sa-source-open' : ''
      }${
        besidePanelOpen
          ? useTaskPaneBesidePortalLayout
            ? ' cc-results-view--sa-beside-portaled'
            : ' cc-results-view--sa-split-side-by-side'
          : ''
      }`}
      style={
        besidePanelOpen && !useTaskPaneBesidePortalLayout
          ? ({
              ['--cc-sa-beside-pdf-pct' as string]: `${saBesidePdfWidthPct}%`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {salesAgreementSourceView &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked"
                  role="region"
                  aria-label="Sales Agreement source PDF"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Sales Agreement
                      {salesAgreementSourceView.clauseNumberDisplay
                        ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={() => handleCloseSalesAgreementSource({ alignClause: true })}
                        aria-label="Close Sales Agreement viewer"
                        title="Close Sales Agreement viewer"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {salesAgreementSourceView.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                      </div>
                    )}
                    {salesAgreementSourceView.error && (
                      <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                    )}
                    {salesAgreementSourceView.buffer &&
                      !salesAgreementSourceView.loading &&
                      !salesAgreementSourceView.error && (
                        <PastNegotiationSourcePdfPanel
                          pdfData={salesAgreementSourceView.buffer}
                          initialPage={salesAgreementSourceView.page}
                          totalPagesHint={salesAgreementSourceView.total}
                          searchText={salesAgreementSourceView.searchText}
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost,
            )}
            {renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side"
              role="region"
              aria-label="Sales Agreement source PDF"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Sales Agreement
                  {salesAgreementSourceView.clauseNumberDisplay
                    ? ` — Clause ${salesAgreementSourceView.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={() => handleCloseSalesAgreementSource({ alignClause: true })}
                    aria-label="Close Sales Agreement viewer"
                    title="Close Sales Agreement viewer"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {salesAgreementSourceView.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading Sales Agreement…</p>
                  </div>
                )}
                {salesAgreementSourceView.error && (
                  <p className="cc-source-pdf-error">{salesAgreementSourceView.error}</p>
                )}
                {salesAgreementSourceView.buffer &&
                  !salesAgreementSourceView.loading &&
                  !salesAgreementSourceView.error && (
                    <PastNegotiationSourcePdfPanel
                      pdfData={salesAgreementSourceView.buffer}
                      initialPage={salesAgreementSourceView.page}
                      totalPagesHint={salesAgreementSourceView.total}
                      searchText={salesAgreementSourceView.searchText}
                    />
                  )}
              </div>
            </div>

            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      {playbookAgentBeside &&
        (showBesidePortal ? (
          <>
            {createPortal(
              <div className="cc-sa-mcc-portal-pdf-root">
                <div
                  className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--mcc-docked cc-sa-source-split-panel--playbook-agent"
                  role="region"
                  aria-label="Playbook agent chat"
                >
                  <div className="cc-sa-source-split-header">
                    <span className="cc-sa-source-split-title">
                      Ask Agent
                      {playbookAgentBeside.clauseNumberDisplay
                        ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                        : ''}
                    </span>
                    <div className="cc-sa-source-split-header-actions">
                      <button
                        type="button"
                        className="cc-sa-source-split-close"
                        onClick={() => handleClosePlaybookAgent({ alignClause: true })}
                        aria-label="Close playbook agent"
                        title="Close playbook agent"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <div className="cc-sa-source-split-body">
                    {playbookAgentBeside.loading && (
                      <div className="cc-source-loading" role="status" aria-live="polite">
                        <span className="cc-source-loading-spinner" aria-hidden />
                        <p className="cc-source-loading-text">Loading playbook…</p>
                      </div>
                    )}
                    {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                      <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                    )}
                    {!playbookAgentBeside.loading &&
                      !playbookAgentBeside.error &&
                      playbookAgentBeside.contextBody && (
                        <PlaybookAgentBesidePanel
                          key={playbookAgentBeside.sessionKey}
                          projectLabel={playbookAgentBeside.projectLabel}
                          recordCount={playbookAgentBeside.recordCount}
                          contextBody={playbookAgentBeside.contextBody}
                          clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                          askLanguage={playbookAgentBeside.askLanguage}
                          initialQuestion={playbookAgentBeside.initialQuestion}
                          openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                          awaitingDeepResearchConsent={
                            playbookAgentBeside.awaitingDeepResearchConsent
                          }
                          awaitingContextSourceChoice={
                            playbookAgentBeside.awaitingContextSourceChoice
                          }
                          contextSource={playbookAgentBeside.contextSource}
                          onChooseContextSource={handleChooseAskAgentContextSource}
                          playbookRows={playbookAgentBeside.playbookRows}
                          matchRankToArchiveRecordNum={
                            playbookAgentBeside.matchRankToArchiveRecordNum
                          }
                          projectId={projectId ?? projectIdProp ?? null}
                          playbookDomain={detectedDomain ?? 'construction-loan'}
                          clauseContextSnippet={playbookAgentBeside.contextBody.slice(0, 2000)}
                          onApplySuggestedEdit={handleApplyAgentSuggestedEdit}
                          isApplyingSuggestedEdit={
                            agentAiStreamingClauseId === playbookAgentBeside.clauseId
                          }
                        />
                      )}
                  </div>
                </div>
              </div>,
              pdfBesideDockHost!,
            )}
            {!salesAgreementSourceView && renderBesideResizeHandlePortal(besideHandleSlotHost)}
          </>
        ) : (
          <>
            <div
              className="cc-sa-source-split-panel cc-sa-source-split-panel--side-by-side cc-sa-source-split-panel--playbook-agent"
              role="region"
              aria-label="Playbook agent chat"
            >
              <div className="cc-sa-source-split-header">
                <span className="cc-sa-source-split-title">
                  Ask Agent
                  {playbookAgentBeside.clauseNumberDisplay
                    ? ` — Clause ${playbookAgentBeside.clauseNumberDisplay}`
                    : ''}
                </span>
                <div className="cc-sa-source-split-header-actions">
                  <button
                    type="button"
                    className="cc-sa-source-split-close"
                    onClick={() => handleClosePlaybookAgent({ alignClause: true })}
                    aria-label="Close playbook agent"
                    title="Close playbook agent"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div className="cc-sa-source-split-body">
                {playbookAgentBeside.loading && (
                  <div className="cc-source-loading" role="status" aria-live="polite">
                    <span className="cc-source-loading-spinner" aria-hidden />
                    <p className="cc-source-loading-text">Loading playbook…</p>
                  </div>
                )}
                {playbookAgentBeside.error && !playbookAgentBeside.loading && (
                  <p className="cc-source-pdf-error">{playbookAgentBeside.error}</p>
                )}
                {!playbookAgentBeside.loading &&
                  !playbookAgentBeside.error &&
                  playbookAgentBeside.contextBody && (
                    <PlaybookAgentBesidePanel
                      key={playbookAgentBeside.sessionKey}
                      projectLabel={playbookAgentBeside.projectLabel}
                      recordCount={playbookAgentBeside.recordCount}
                      contextBody={playbookAgentBeside.contextBody}
                      clauseNumberDisplay={playbookAgentBeside.clauseNumberDisplay}
                      askLanguage={playbookAgentBeside.askLanguage}
                      initialQuestion={playbookAgentBeside.initialQuestion}
                      openingAssistantMessage={playbookAgentBeside.openingAssistantMessage}
                      awaitingDeepResearchConsent={
                        playbookAgentBeside.awaitingDeepResearchConsent
                      }
                      awaitingContextSourceChoice={
                        playbookAgentBeside.awaitingContextSourceChoice
                      }
                      contextSource={playbookAgentBeside.contextSource}
                      onChooseContextSource={handleChooseAskAgentContextSource}
                      playbookRows={playbookAgentBeside.playbookRows}
                      matchRankToArchiveRecordNum={
                        playbookAgentBeside.matchRankToArchiveRecordNum
                      }
                      projectId={projectId ?? projectIdProp ?? null}
                      playbookDomain={detectedDomain ?? 'construction-loan'}
                      clauseContextSnippet={playbookAgentBeside.contextBody.slice(0, 2000)}
                      onApplySuggestedEdit={handleApplyAgentSuggestedEdit}
                      isApplyingSuggestedEdit={
                        agentAiStreamingClauseId === playbookAgentBeside.clauseId
                      }
                    />
                  )}
              </div>
            </div>
            {!showBesidePortal && besideResizeHandleEl}
          </>
        ))}

      <div
        ref={resultsMainColumnRef}
        className={`cc-results-main-column${
          besidePanelOpen
            ? ` cc-results-main-column--side-by-side${
                useTaskPaneBesidePortalLayout ? ' cc-results-main-column--mcc-beside-portal' : ''
              }`
            : ' cc-results-main-column--stacked'
        }`}
      >
      {/* Scrolls away with page (same level as Project Settings in negotiator layout) */}
      <div ref={workflowStepsScrollAnchorRef} className="cc-results-steps-scroll">
        <div className="cc-run-steps-panel">
          <div className={`cc-run-step-item cc-run-${stepStatuses.step0}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>✓</span>
            <span className="cc-run-label">Identify sides</span>
            <span className="cc-run-timer" />
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step1}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step1 === 'complete' ? '✓' : stepStatuses.step1 === 'running' ? '◌' : stepStatuses.step1 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">Identify client requests</span>
            <span className="cc-run-timer">{stepTimes.step1}</span>
          </div>
          <div className={`cc-run-step-item cc-run-${stepStatuses.step2}`}>
            <span className="cc-run-arrow" aria-hidden>&#8250;</span>
            <span className="cc-run-status-icon" aria-hidden>
              {stepStatuses.step2 === 'complete' ? '✓' : stepStatuses.step2 === 'running' ? '◌' : stepStatuses.step2 === 'error' ? '✕' : '○'}
            </span>
            <span className="cc-run-label">Cross-check analysis</span>
            <span className="cc-run-timer">{stepTimes.step2}</span>
          </div>
        </div>
      </div>

      {/* Sticky: progress + tabs only (matches ClausesEditor sticky header pattern) */}
      <div className="cc-results-sticky-header" ref={stickyHeaderRef}>
        <div className="cc-results-header">
          {brainShareTotalCount > 0 && (
            <button
              type="button"
              className="cc-brainshare-header-btn"
              onClick={() => setShowBrainShareReviewModal(true)}
              title={
                brainSharePendingCount > 0
                  ? `${brainSharePendingCount} BrainShare ${brainSharePendingCount === 1 ? 'entry' : 'entries'} pending approval`
                  : 'View BrainShare entries'
              }
              aria-label="View BrainShare entries"
            >
              <Brain className="cc-brainshare-header-btn-icon" aria-hidden />
              {brainSharePendingCount > 0 && (
                <span className="cc-brainshare-header-badge">
                  {brainSharePendingCount > 99 ? '99+' : brainSharePendingCount}
                </span>
              )}
            </button>
          )}
          <div className="cc-results-progress">
            <div className="cc-results-progress-bar">
              <div
                className="cc-results-progress-fill"
                style={{
                  width: progress.total > 0 ? `${progress.pct}%` : '0%',
                }}
              />
            </div>
            <div className="cc-results-progress-text">
              Progress: {progress.current}/{progress.total} ({progress.pct}%)
            </div>
          </div>
          <div className="cc-results-header-actions">
            <button
              className="cc-refresh-btn"
              onClick={() => { void loadResults(); }}
              disabled={loading}
              title="Refresh results"
            >
              &#x21BB;
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="cc-results-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`cc-tab ${tab.cssClass}${activeTab === tab.id ? ' cc-tab-active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
              aria-pressed={activeTab === tab.id}
              aria-label={`Show ${tab.label}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {showClauseSubFilters && (
          <div className="cc-results-subfilters-wrap">
            <div className="cc-results-subfilters">
              <div className="cc-results-subfilters-main">
                <span className="cc-results-subfilters-label">Filter</span>
                <div className="cc-results-subfilters-chips" role="group" aria-label="Clause filters">
                  <button
                    type="button"
                    className={`cc-subfilter-chip${filterFinalVersion ? ' cc-subfilter-chip--active' : ''}`}
                    onClick={() => setFilterFinalVersion((prev) => !prev)}
                    aria-pressed={filterFinalVersion}
                  >
                    Final version
                  </button>
                  <div className="cc-similarity-filter" ref={similarityFilterRef}>
                    <button
                      type="button"
                      className={`cc-subfilter-chip cc-subfilter-chip--similarity${
                        filterMinSimilarity != null || similarityPanelOpen ? ' cc-subfilter-chip--active' : ''
                      }`}
                      onClick={handleSimilarityChipClick}
                      aria-pressed={filterMinSimilarity != null || similarityPanelOpen}
                      aria-expanded={similarityPanelOpen}
                    >
                      Similarity
                      {filterMinSimilarity != null && (
                        <span className="cc-subfilter-chip-value">
                          {formatSimilarityFilterLabel(filterMinSimilarity)}
                        </span>
                      )}
                    </button>
                    {similarityPanelOpen && (
                      <div className="cc-similarity-panel" role="dialog" aria-label="Similarity filter">
                        <div className="cc-similarity-panel-header">
                          <span className="cc-similarity-panel-title">Minimum similarity</span>
                          <span className="cc-similarity-panel-value">
                            {formatSimilarityFilterLabel(similaritySliderValue)}
                          </span>
                        </div>
                        <input
                          type="range"
                          className="cc-similarity-slider"
                          min={SIMILARITY_FILTER_MIN}
                          max={SIMILARITY_FILTER_MAX}
                          step={1}
                          value={similaritySliderValue}
                          onChange={handleSimilaritySliderChange}
                          aria-label="Minimum similarity percentage"
                          aria-valuemin={SIMILARITY_FILTER_MIN}
                          aria-valuemax={SIMILARITY_FILTER_MAX}
                          aria-valuenow={similaritySliderValue}
                        />
                        <div className="cc-similarity-presets" role="group" aria-label="Similarity quick choices">
                          {SIMILARITY_FILTER_PRESETS.map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              className={`cc-similarity-preset${
                                filterMinSimilarity === preset ? ' cc-similarity-preset--active' : ''
                              }`}
                              onClick={() => applySimilarityThreshold(preset)}
                              aria-pressed={filterMinSimilarity === preset}
                            >
                              {formatSimilarityFilterLabel(preset)}
                            </button>
                          ))}
                        </div>
                        {filterMinSimilarity != null && (
                          <button
                            type="button"
                            className="cc-similarity-clear"
                            onClick={handleClearSimilarityFilter}
                          >
                            Clear similarity
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {availableAuthors.length > 0 && (
                    <div className="cc-authors-filter" ref={authorsFilterRef}>
                      <button
                        type="button"
                        className={`cc-subfilter-chip cc-subfilter-chip--authors${
                          filterSelectedAuthors !== null || authorsPanelOpen ? ' cc-subfilter-chip--active' : ''
                        }`}
                        onClick={handleAuthorsChipClick}
                        aria-pressed={filterSelectedAuthors !== null || authorsPanelOpen}
                        aria-expanded={authorsPanelOpen}
                      >
                        Authors
                        {authorFilterLabel && (
                          <span className="cc-subfilter-chip-value">{authorFilterLabel}</span>
                        )}
                      </button>
                      {authorsPanelOpen && (
                        <div className="cc-authors-panel" role="dialog" aria-label="Authors filter">
                          <div className="cc-authors-panel-header">
                            <span className="cc-authors-panel-title">Past negotiation authors</span>
                          </div>
                          <button
                            type="button"
                            className={`cc-authors-all-btn${
                              filterSelectedAuthors === null ? ' cc-authors-all-btn--active' : ''
                            }`}
                            onClick={handleSelectAllAuthors}
                            aria-pressed={filterSelectedAuthors === null}
                          >
                            All
                          </button>
                          <div className="cc-authors-list" role="group" aria-label="Select authors">
                            {availableAuthors.map((author) => {
                              const isChecked =
                                filterSelectedAuthors === null || filterSelectedAuthors.has(author);
                              return (
                                <label key={author} className="cc-authors-option">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggleAuthorFilter(author)}
                                  />
                                  <span className="cc-authors-option-label">{author}</span>
                                </label>
                              );
                            })}
                          </div>
                          {filterSelectedAuthors !== null && (
                            <button
                              type="button"
                              className="cc-authors-clear"
                              onClick={handleClearAuthorsFilter}
                            >
                              Clear authors
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {availableYears.length > 0 && (
                    <div className="cc-time-filter" ref={timeFilterRef}>
                      <button
                        type="button"
                        className={`cc-subfilter-chip cc-subfilter-chip--time${
                          filterSelectedYears !== null || timePanelOpen ? ' cc-subfilter-chip--active' : ''
                        }`}
                        onClick={handleTimeChipClick}
                        aria-pressed={filterSelectedYears !== null || timePanelOpen}
                        aria-expanded={timePanelOpen}
                      >
                        Time
                        {timeFilterLabel && (
                          <span className="cc-subfilter-chip-value">{timeFilterLabel}</span>
                        )}
                      </button>
                      {timePanelOpen && (
                        <div className="cc-time-panel" role="dialog" aria-label="Time filter">
                          <div className="cc-time-panel-header">
                            <span className="cc-time-panel-title">Past negotiation time</span>
                          </div>
                          <button
                            type="button"
                            className={`cc-time-all-btn${
                              filterSelectedYears === null ? ' cc-time-all-btn--active' : ''
                            }`}
                            onClick={handleSelectAllTime}
                            aria-pressed={filterSelectedYears === null}
                          >
                            All
                          </button>
                          <div className="cc-authors-list" role="group" aria-label="Select years">
                            {availableYears.map((year) => {
                              const isChecked =
                                filterSelectedYears === null || filterSelectedYears.has(year);
                              return (
                                <label key={year} className="cc-authors-option">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => handleToggleYearFilter(year)}
                                  />
                                  <span className="cc-authors-option-label">{year}</span>
                                </label>
                              );
                            })}
                          </div>
                          {filterSelectedYears !== null && (
                            <button
                              type="button"
                              className="cc-time-clear"
                              onClick={handleClearTimeFilter}
                            >
                              Clear time
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {hasActiveSubFilters && (
                    <button
                      type="button"
                      className="cc-subfilter-clear"
                      onClick={handleClearSubFilters}
                      aria-label="Clear filters"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {hasActiveClauseSubFilters && (
                  <span className="cc-results-subfilters-count">
                    Showing {displayedClauses.length} of {filteredClauses.length}
                  </span>
                )}
              </div>
              {hasActiveClauseSubFilters && (
                <div className="cc-results-subfilters-actions">
                  <button
                    type="button"
                    className="cc-subfilter-open-btn"
                    onClick={handleOpenAllFiltered}
                    disabled={bulkOpening || bulkClosing || bulkClosePrompt != null || openableCount === 0}
                    title="Unlock all locked clauses matching the current filters"
                    aria-label={`Open all ${openableCount} locked clause${openableCount === 1 ? '' : 's'}`}
                  >
                    <svg
                      className="cc-subfilter-open-btn-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      aria-hidden
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z"
                      />
                    </svg>
                    {bulkOpening ? 'Opening…' : `Open all (${openableCount})`}
                  </button>
                  <button
                    type="button"
                    className="cc-subfilter-close-btn"
                    onClick={handleCloseFiltered}
                    disabled={bulkClosing || bulkOpening || bulkClosePrompt != null || closableCount === 0}
                    title="Lock all clauses matching the current filters"
                    aria-label={`Close ${closableCount} clause${closableCount === 1 ? '' : 's'}`}
                  >
                    <svg
                      className="cc-subfilter-close-btn-lock"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {bulkClosing ? 'Closing…' : `Close (${closableCount})`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Overlay sync indicator — does not shift clause cards */}
        {syncingIndices && (
          <div className="cc-sync-banner cc-sync-banner--overlay" role="status" aria-live="polite">
            <span className="cc-sync-banner-spinner" aria-hidden />
            <span className="cc-sync-banner-text">Syncing paragraph locations…</span>
          </div>
        )}
      </div>

      {showDebugLogPanel && (
        <div className="cc-debug-log-bar" role="region" aria-label="Debug logging">
          <div className="cc-debug-log-actions">
            <span className="cc-debug-log-meta">
              Session {getRemoteDebugSessionId()} · {remoteDebugEntryCount} entries
            </span>
            <button
              type="button"
              className="cc-debug-log-copy"
              disabled={remoteDebugCopying || remoteDebugEntryCount === 0}
              onClick={async () => {
                setRemoteDebugCopying(true);
                setRemoteDebugCopyStatus(null);
                try {
                  const result = await copyDebugLogToClipboard();
                  if (result.ok) {
                    setRemoteDebugCopyStatus(
                      `Copied ${result.charCount?.toLocaleString() ?? ''} characters — paste into your email.`,
                    );
                  } else {
                    setRemoteDebugCopyStatus(result.error ?? 'Copy failed.');
                  }
                } finally {
                  setRemoteDebugCopying(false);
                }
              }}
            >
              {remoteDebugCopying ? 'Copying…' : 'Copy logs'}
            </button>
            <button
              type="button"
              className="cc-debug-log-clear"
              onClick={() => {
                clearDebugLog();
                setRemoteDebugEntryCount(0);
                setRemoteDebugCopyStatus(null);
              }}
            >
              Clear
            </button>
          </div>
          {remoteDebugCopyStatus && (
            <div className="cc-debug-log-status" role="status">
              {remoteDebugCopyStatus}
            </div>
          )}
          <p className="cc-debug-log-hint">
            Reproduce the issue, click Copy logs, then paste into an email to support. Turn off in
            Account → Debug logging.
          </p>
        </div>
      )}

      {/* Error */}
      {error && <div className="cc-error">{error}</div>}

      {/* Subtle reconnecting indicator shown during transient poll failures (results stay visible) */}
      {pollingError && !error && (
        <div className="cc-polling-error">
          <span className="cc-action-banner-spinner" aria-hidden />
          <span>Reconnecting…</span>
        </div>
      )}

      {/* Polling stopped banner — shown after too many consecutive errors; lets user restart polling */}
      {pollingStopped && !error && (
        <div className="cc-action-banner cc-action-banner--error" role="status">
          <span>Updates paused due to connection issues.</span>
          <button
            className="cc-retry-poll-btn"
            onClick={() => {
              setPollingStopped(false);
              setPollingError(false);
              setPollRetryKey((k) => k + 1);
            }}
          >
            Retry
          </button>
        </div>
      )}

      {actionBanner && (
        <div
          className={`cc-action-banner cc-action-banner--${actionBanner.type}`}
          role="status"
        >
          {actionBanner.type === 'loading' && <span className="cc-action-banner-spinner" aria-hidden />}
          <span>{actionBanner.message}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="cc-loading">
          <div className="cc-spinner" />
          <span className="cc-loading-text">Loading cross-check results...</span>
          <span className="cc-loading-sub">Please wait while we fetch your data</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && displayedClauses.length === 0 && (
        <div className="cc-empty-state">
          <span className="cc-empty-icon">&#128196;</span>
          <span className="cc-empty-title">
            {clauses.length === 0
              ? 'No cross-check results yet'
              : hasActiveSubFilters && filteredClauses.length > 0
              ? 'No clauses match the selected filters'
              : `No clauses in ${
                  activeTab === 'sales_agreement'
                    ? 'Sales Agreement'
                    : activeTab === 'appendix_a'
                    ? 'Appendix A'
                    : activeTab === 'saved'
                    ? 'Saved'
                    : activeTab === 'commercial'
                    ? 'Commercial'
                    : activeTab === 'legal'
                    ? 'Legal'
                    : activeTab === 'other'
                    ? 'Other'
                    : ''
                } category`}
          </span>
          {hasActiveSubFilters && filteredClauses.length > 0 && (
            <button
              type="button"
              className="cc-subfilter-clear cc-subfilter-clear--empty"
              onClick={handleClearSubFilters}
            >
              Clear filters
            </button>
          )}
          {clauses.length === 0 && (
            <span className="cc-empty-subtitle">
              {workflowStep == null && !workflowProgressBlob
                ? 'Cross-check has not been started yet. Re-select this sub-project to begin.'
                : 'Run cross-check in the dashboard to see results here'}
            </span>
          )}
        </div>
      )}

      {/* Clause cards — key by activeTab so list remounts and updates when filter changes */}
      {!loading && displayedClauses.length > 0 && (
        <div
          key={`${activeTab}-${filterFinalVersion}-${filterMinSimilarity ?? 'any'}`}
          className="cc-results-list"
          role="list"
        >
          {displayedClauses.map((clause, idx) => {
            const isSaSourceActive =
              !!salesAgreementSourceView && salesAgreementSourceView.clauseId === clause.id;
            const isSaSourceLoading =
              isSaSourceActive && !!salesAgreementSourceView?.loading;
            return (
              <CrossCheckClauseCard
                key={clause.id}
                clause={clause}
                index={idx}
                projectId={projectId ?? projectIdProp}
                subProjectId={subProjectId}
                onToggleLock={handleToggleLock}
                isUpdatingLock={updatingLockId === clause.id}
                onClauseEdited={handleClauseEdited}
                onActionBanner={handleActionBanner}
                onBrainShareUpdated={handleBrainShareUpdated}
                onResponseSubmitted={handleResponseSubmitted}
                onResponseUndone={handleResponseUndone}
                submitRevertContextForClause={
                  submitRevertContextByClauseId[clause.id] ??
                  submitRevertContextFromSubmissionStatus(clause.submission_status)
                }
                pastAgreementPlaybookXmlById={playbookXmlById}
                pastAgreementPlaybookSideById={playbookSideById}
                pastAgreementPlaybookFileNameById={playbookFileNameById}
                pastAgreementPlaybookFileIdById={playbookFileIdById}
                pastAgreementPlaybookFinalVersionById={playbookFinalVersionById}
                pastAgreementPlaybookPlainTextById={playbookPlainTextById}
                pastAgreementPlaybookAuthorById={playbookAuthorById}
                pastAgreementAuthorFilter={filterSelectedAuthors}
                pastAgreementPlaybookTimestampById={playbookTimestampById}
                pastAgreementTimeFilter={filterSelectedYears}
                pastAgreementPlaybooksLoading={playbooksBatchLoading}
                pastAgreementDomain={detectedDomain ?? 'construction-loan'}
                onGoToSalesAgreementSource={handleOpenSalesAgreementSource}
                isSalesAgreementSourceActive={isSaSourceActive}
                isSalesAgreementSourceLoading={isSaSourceLoading}
                kebabVariant={isConstructionLoan ? 'construction-loan' : 'mcc'}
                crossCheckSolution={crossCheckSolution}
                onMoveClause={isConstructionLoan ? undefined : handleMoveClause}
                onMoveClauseMatter={isConstructionLoan ? handleMoveClauseMatter : undefined}
                onAnalyzeAgain={handleAnalyzeAgainOpen}
                onAskAgent={projectId ?? projectIdProp ? handleOpenPlaybookAgent : undefined}
                isMutating={!!mutatingClauseIds[clause.id]}
                externalAiStreaming={agentAiStreamingClauseId === clause.id}
                externalEditedXml={agentSuggestedXmlByClauseId[clause.id] ?? null}
                cardHeaderRef={setClauseCardHeaderRef(clause.id)}
              />
            );
          })}
        </div>
      )}
      </div>

      {bulkClosePrompt && (
        <BulkCloseConfirmModal
          trackChangesCount={bulkClosePrompt.withTrackChanges.length}
          totalCount={bulkClosePrompt.toLock.length}
          busy={bulkClosing}
          busyMessage={bulkCloseBusyMessage}
          onAcceptAndClose={() => { void handleBulkCloseAcceptAndClose(); }}
          onCloseWithoutAccept={() => { void handleBulkCloseWithoutAccept(); }}
          onCancel={() => {
            if (bulkClosing) return;
            setBulkClosePrompt(null);
            setBulkCloseBusyMessage(null);
          }}
        />
      )}

      <AnalyzeAgainModal
        clause={analyzeAgainClause}
        variant={analyzeAgainVariant}
        onCancel={() => setAnalyzeAgainClause(null)}
        onSubmit={handleAnalyzeAgainSubmit}
      />

      {subProjectId && (
        <BrainShareReviewModal
          isOpen={showBrainShareReviewModal}
          onClose={() => setShowBrainShareReviewModal(false)}
          subProjectId={subProjectId}
          onCountsChanged={() => { void fetchBrainShareCounts(); }}
          onNotify={handleActionBanner}
        />
      )}
    </div>
  );
};

export default CrossCheckResultsView;
